import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowLeft,
  Archive,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleDot,
  Copy,
  ExternalLink,
  Eye,
  FileClock,
  FileText,
  Gavel,
  Landmark,
  LoaderCircle,
  Mail,
  MapPin,
  MessageCircle,
  Paperclip,
  Phone,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Sparkles,
  Trash2,
  UsersRound,
  X,
} from "lucide-react";
import {
  analyzeExpedienteParties,
  compareDeadlineReminders,
  detectSentenceActuacion,
  encodeCuijPath,
  expedienteDetailPath,
  formatCuij,
  isValidCuij,
  matchesApproximateText,
  normalizeCuij,
} from "@tribunalea/shared";
import { AppLink, useRouter } from "../router.jsx";
import { ArgentineDateInput } from "../ArgentineDateInput.jsx";
import { CopyableDate } from "../CopyableDate.jsx";
import { CopyableText } from "../CopyableText.jsx";
import {
  DataTable,
  DataTableAction,
  DataTableBadge,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTablePrimaryCell,
  DataTableRow,
  DataTableStatus,
  DataTableValue,
} from "../DataTable.jsx";
import { DataTableSection } from "../DataTableSection.jsx";
import { formatCredits } from "../credit-format.js";
import { ConfirmDialog } from "../ConfirmDialog.jsx";
import {
  ActuacionDocumentDialog,
  PdfViewer,
} from "./components/ActuacionDocumentOverlays.jsx";
import { ApproximateMatchText } from "./components/ApproximateMatchText.jsx";
import {
  isImportantReminderTone,
  reminderDashboardContentState,
  reminderRefreshPresentation,
  selectDashboardImportantDeadlines,
} from "./lib/expediente-dashboard-reminders.js";
import {
  selectDashboardActuaciones,
  selectDashboardContacts,
} from "./lib/expediente-dashboard-summary.js";
import { orderExpedienteAttentionItems } from "./lib/expediente-attention-order.js";
import {
  deadlineTrafficColor,
  expedienteTrafficState,
  trafficColorToTone,
} from "./lib/expediente-traffic-light.js";
import { uniqueRelatedExpedientes } from "./lib/related-expedientes.js";
import { isUnmanagedExpedienteDetailTab } from "./lib/expediente-detail-tabs.js";
import {
  buildDocumentViewerRoute,
  closeDocumentViewerRoute,
  DOCUMENT_VIEWER_HISTORY_STATE,
  readDocumentViewerRoute,
} from "./lib/documentViewerRoute.js";
import {
  buildExpedienteWhatsappTurnNotices,
  whatsappNoticeForInconsistency,
} from "./lib/expediente-whatsapp-notices.js";
import {
  expedienteSourceAdapterFor,
} from "./integrations/expediente-source-adapters.js";

const AUTOMATIC_EXPEDIENTE_REFRESH_GRACE_MS = 10 * 60 * 1000;

const TIMELINE_FILTERS = [
  { id: "actuaciones", label: "Actuaciones" },
  { id: "escritos", label: "Escritos" },
  { id: "cedulas", label: "Cédulas" },
  { id: "movimientos", label: "Movimientos" },
];
const EXPEDIENTE_DETAIL_TABS = [
  { id: "dashboard", label: "Resumen", slug: "" },
  { id: "information", label: "Información", slug: "informacion" },
  { id: "actuaciones", label: "Actuaciones", slug: "actuaciones" },
  { id: "documents", label: "Documentos", slug: "documentos" },
  { id: "drafts", label: "Escritos y conversaciones", slug: "escritos" },
  {
    id: "reminders",
    label: "Recordatorios",
    slug: "recordatorios",
    legacySlugs: ["plazos"],
  },
];

function expedienteDetailTabPath(
  identifier,
  tabId,
  detailBasePath = "/expedientes",
) {
  const tab = EXPEDIENTE_DETAIL_TABS.find(({ id }) => id === tabId);
  const path = expedienteDetailPath(identifier, tab?.slug || "");
  return detailBasePath === "/seguimientos"
    ? path.replace(/^\/expedientes/u, "/seguimientos")
    : path;
}
const PARTY_GROUPS = [
  {
    id: "actor",
    tableLabel: "Actor",
  },
  {
    id: "defendant",
    tableLabel: "Demandada",
  },
  {
    id: "other",
    tableLabel: "Otros",
  },
];

const dateFormatter = new Intl.DateTimeFormat("es-AR", {
  dateStyle: "medium",
});
const dateTimeFormatter = new Intl.DateTimeFormat("es-AR", {
  dateStyle: "medium",
  timeStyle: "short",
});
const timelineTableDateTimeFormatter = new Intl.DateTimeFormat("es-AR", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

function formatDate(value, withTime = false) {
  if (!value) return "No informado";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No informado";
  return (withTime ? dateTimeFormatter : dateFormatter).format(date);
}

function formatTimelineTableDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const parts = Object.fromEntries(
    timelineTableDateTimeFormatter
      .formatToParts(date)
      .filter(({ type }) => type !== "literal")
      .map(({ type, value: partValue }) => [type, partValue]),
  );
  return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`;
}

function formatDateOnly(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return formatDate(value);
  return dateFormatter.format(
    new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12),
  );
}

function RenderedDate({ as = "button", value, withTime = false }) {
  return (
    <CopyableDate as={as} value={value}>
      {formatDate(value, withTime)}
    </CopyableDate>
  );
}

function RenderedTimelineDate({ dateOnly = false, value }) {
  if (dateOnly) return <RenderedDateOnly as="span" value={value} />;
  return (
    <CopyableDate value={value}>
      {formatTimelineTableDate(value)}
    </CopyableDate>
  );
}

function RenderedDateOnly({ as = "button", value }) {
  return (
    <CopyableDate as={as} value={value}>
      {formatDateOnly(value)}
    </CopyableDate>
  );
}

function hasRecentExpedienteCheck(expediente, now = Date.now()) {
  const status = expediente?.dataSync?.details?.status;
  if (status && status !== "ready") return false;
  const value =
    expediente?.detailsRefreshedAt || expediente?.praxisRefreshedAt;
  const checkedAt = value ? new Date(value).getTime() : Number.NaN;
  return (
    Number.isFinite(checkedAt) &&
    now - checkedAt >= 0 &&
    now - checkedAt < AUTOMATIC_EXPEDIENTE_REFRESH_GRACE_MS
  );
}

function todayInMendoza() {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Argentina/Mendoza",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .formatToParts(new Date())
      .filter(({ type }) => type !== "literal")
      .map(({ type, value }) => [type, value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function getActuacionCategory(actuacion) {
  if (actuacion.esCedula) return "Cédula";
  if (actuacion.esEscrito) return "Escrito";
  return "Actuación";
}

function getActuacionFilter(actuacion) {
  if (actuacion.esCedula) return "cedulas";
  if (actuacion.esEscrito) return "escritos";
  return "actuaciones";
}

function actuacionDocumentId(actuacion) {
  return String(actuacion.actId || actuacion.mexpId || "");
}

function timelineDomId(item) {
  return `timeline-${String(item.entryId).replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

function timelineSourceIds(item) {
  return [
    item.actId,
    item.mexpId,
    item.numero,
    item.preExpId,
  ]
    .filter((value) => value !== undefined && value !== null && value !== "")
    .map(String);
}

function timelineSearchValues(item) {
  return item.entryType === "movimiento"
    ? [
        item.descripcion,
        item.origen,
        item.destino,
        item.mexpId,
        "movimiento",
      ]
    : [
        item.titulo,
        item.tipo,
        item.numero,
        item.actId,
        item.organismoCreador,
        item.firmantes,
        item.estadoNotificacion,
        getActuacionCategory(item),
      ];
}

function buildTimelineItems(expediente = {}) {
  const actuaciones = (expediente?.actuaciones || []).map(
    (actuacion, index) => ({
      ...actuacion,
      category: getActuacionFilter(actuacion),
      entryId: `actuacion:${
        actuacion.actId ||
        actuacion.mexpId ||
        actuacion.numero ||
        index
      }:${index}`,
      entryType: "actuacion",
      occurredAt: actuacion.fechaFirma || actuacion.fechaPub || null,
    }),
  );
  const movimientos = (expediente?.movimientos || []).map(
    (movimiento, index) => ({
      ...movimiento,
      category: "movimientos",
      entryId: `movimiento:${
        movimiento.mexpId || movimiento.preExpId || index
      }:${index}`,
      entryType: "movimiento",
      occurredAt: movimiento.fecha || null,
    }),
  );
  return [...actuaciones, ...movimientos].sort((left, right) => {
    const leftTime = new Date(left.occurredAt || 0).getTime() || 0;
    const rightTime = new Date(right.occurredAt || 0).getTime() || 0;
    return rightTime - leftTime || left.entryId.localeCompare(right.entryId);
  });
}

function mergeExpedienteSummary(current, next) {
  if (!current || !next) return next;
  return {
    ...next,
    ...(!Object.hasOwn(next, "actuaciones") && current.actuaciones
      ? { actuaciones: current.actuaciones }
      : {}),
    ...(!Object.hasOwn(next, "movimientos") && current.movimientos
      ? { movimientos: current.movimientos }
      : {}),
  };
}

function DetailValue({ label, value, icon: Icon }) {
  return (
    <div className="expediente-detail-value">
      <span>{Icon && <Icon size={15} />}{label}</span>
      <strong>{value || "No informado"}</strong>
    </div>
  );
}

async function copyToClipboard(value) {
  const text = String(value || "").trim();
  if (!text) return;
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall back for browsers that expose the API but block its use.
    }
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

function CopyValueButton({ value, label = "valor" }) {
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef(null);

  useEffect(
    () => () => window.clearTimeout(copiedTimeoutRef.current),
    [],
  );

  const handleCopy = async (event) => {
    event?.stopPropagation();
    try {
      await copyToClipboard(value);
      setCopied(true);
      window.clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = window.setTimeout(
        () => setCopied(false),
        1600,
      );
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      className={`copy-value-button ${copied ? "is-copied" : ""}`}
      aria-label={copied ? `${label} copiado` : `Copiar ${label}`}
      title={copied ? "Copiado" : `Copiar ${label}`}
      onClick={handleCopy}
    >
      {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
    </button>
  );
}

function WhatsappNoticeCopyButton({ notice, compact = false }) {
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef(null);

  useEffect(
    () => () => window.clearTimeout(copiedTimeoutRef.current),
    [],
  );

  if (!notice?.message) return null;

  const handleCopy = async () => {
    try {
      await copyToClipboard(notice.message);
      setCopied(true);
      window.clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = window.setTimeout(
        () => setCopied(false),
        1800,
      );
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      className={`whatsapp-notice-copy-button ${compact ? "is-compact" : ""} ${copied ? "is-copied" : ""}`}
      onClick={handleCopy}
      title={copied ? "Aviso copiado" : "Copiar aviso para WhatsApp"}
    >
      {copied ? <CheckCircle2 size={14} /> : <MessageCircle size={14} />}
      {copied ? "Aviso copiado" : "Copiar aviso para WhatsApp"}
    </button>
  );
}

function DeadlineDraftDialog({ draft, onClose, onOpenGenerator }) {
  const [copied, setCopied] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState("");
  const copiedTimeoutRef = useRef(null);

  useEffect(() => {
    setCopied(false);
    setOpening(false);
    setError("");
  }, [draft]);

  useEffect(() => {
    if (!draft) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [draft, onClose]);

  useEffect(
    () => () => window.clearTimeout(copiedTimeoutRef.current),
    [],
  );

  if (!draft) return null;

  const handleCopy = async () => {
    try {
      await copyToClipboard(draft.body || "");
      setCopied(true);
      window.clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = window.setTimeout(
        () => setCopied(false),
        1800,
      );
    } catch {
      setCopied(false);
    }
  };

  const handleOpenGenerator = async () => {
    setOpening(true);
    setError("");
    try {
      await onOpenGenerator(draft);
      onClose();
    } catch (openError) {
      setError(openError.message || "No pudimos crear el documento.");
    } finally {
      setOpening(false);
    }
  };

  const isFiling = draft.kind === "filing";

  return (
    <div
      className="modal-backdrop deadline-draft-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        aria-labelledby="deadline-draft-title"
        aria-modal="true"
        className="dialog-card deadline-draft-dialog"
        role="dialog"
      >
        <button
          aria-label="Cerrar borrador"
          className="icon-button dialog-close"
          onClick={onClose}
          type="button"
        >
          <X size={18} />
        </button>
        <div className="dialog-icon primary">
          {isFiling ? <FileText size={20} /> : <MessageCircle size={20} />}
        </div>
        <span className="deadline-draft-kind">Elegí cómo continuar</span>
        <h2 id="deadline-draft-title">{draft.title}</h2>
        {draft.recipient && (
          <p className="deadline-draft-recipient">
            Destinatario sugerido: <strong>{draft.recipient}</strong>
          </p>
        )}
        <div className="deadline-draft-destinations">
          <button
            autoFocus
            className="deadline-draft-destination is-available"
            disabled={opening}
            onClick={handleOpenGenerator}
            type="button"
          >
            <FileText size={22} />
            <span>
              <strong>Generador de Tribunalea</strong>
              <small>Editar, usar asistencia IA y descargar en Word.</small>
            </span>
            {opening ? <LoaderCircle className="spin" size={17} /> : <ChevronRight size={17} />}
          </button>
          <button
            className="deadline-draft-destination"
            disabled
            type="button"
          >
            <Sparkles size={22} />
            <span>
              <strong>Continuar con ChatGPT</strong>
              <small>Abrir una conversación con el contexto preparado.</small>
            </span>
            <em>Próximamente</em>
          </button>
        </div>
        {draft.body && (
          <details className="deadline-draft-preview">
            <summary>Ver texto inicial sugerido</summary>
            <p>{draft.body}</p>
          </details>
        )}
        {draft.notice && (
          <p className="deadline-draft-notice">{draft.notice}</p>
        )}
        {error && <div className="inline-error">{error}</div>}
        <div className="dialog-actions">
          <button className="button secondary" onClick={onClose} type="button">
            Cerrar
          </button>
          <button
            className="button primary"
            disabled={!draft.body?.trim()}
            onClick={handleCopy}
            type="button"
          >
            {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
            {copied ? "Copiado" : "Copiar texto sugerido"}
          </button>
        </div>
      </section>
    </div>
  );
}

const CONTACT_ENTITY_LABELS = {
  party: "Cliente o parte",
  lawyer: "Abogado/a",
  expert: "Perito",
  witness: "Testigo/a",
  judge: "Juez/a",
  court: "Tribunal",
  organization: "Organización",
  other: "Otro",
};

const CONTACT_KIND_LABELS = {
  phone: "Teléfono",
  email: "Email",
  address: "Domicilio",
  electronic_address: "Domicilio electrónico",
  website: "Sitio web",
  other: "Otro dato",
};

const CONTACT_KIND_ICONS = {
  phone: Phone,
  email: Mail,
  address: MapPin,
  electronic_address: Mail,
  website: ExternalLink,
  other: FileText,
};

const CONTACT_KIND_ORDER = [
  "phone",
  "email",
  "electronic_address",
  "address",
  "website",
  "other",
];

const DIRECTORY_GROUPS = [
  { id: "all", label: "Todos" },
  { id: "party", label: "Partes" },
  { id: "lawyer", label: "Abogados" },
  { id: "expert", label: "Peritos" },
  { id: "witness", label: "Testigos" },
  { id: "court", label: "Tribunal" },
  { id: "other", label: "Otros" },
  { id: "previous", label: "Anteriores" },
];

const DIRECTORY_GROUP_ORDER = [
  "party",
  "lawyer",
  "expert",
  "witness",
  "court",
  "other",
];

const HISTORICAL_ROLE_PATTERN =
  /removid|excusad|dej(?:ada|ado) sin efecto|reemplazad|apartad|revocad|cesad|no acept[oó]|falta de aceptaci[oó]n/i;

function directoryRoleIsHistorical(role) {
  return HISTORICAL_ROLE_PATTERN.test(String(role || ""));
}

function directoryGroupFor(entry) {
  const role = [entry.role, ...(entry.roleDetails || []).map(({ label }) => label)]
    .filter(Boolean)
    .join(" ");
  if (entry.entityType === "party") return "party";
  if (entry.entityType === "lawyer" || /abogad|apoderad|patrocin/i.test(role)) {
    return "lawyer";
  }
  if (entry.entityType === "expert" || /perit|consultor t[eé]cnico/i.test(role)) {
    return "expert";
  }
  if (entry.entityType === "witness" || /testig/i.test(role)) return "witness";
  if (
    ["judge", "court"].includes(entry.entityType) ||
    /juez|jueza|conjuez|camarista|tribunal|secretar|prosecretar|receptor|auxiliar|funcionari|emplead/i.test(role)
  ) {
    return "court";
  }
  return "other";
}

function directoryRoleDetails(entry) {
  const details = entry.roleDetails?.length
    ? entry.roleDetails
    : entry.role
      ? [{
          label: entry.role,
          representedParty: entry.representedParty || null,
          historical: directoryRoleIsHistorical(entry.role),
        }]
      : [];
  return details.filter((detail, index, all) =>
    all.findIndex((candidate) =>
      normalizedDirectoryName(candidate.label) ===
        normalizedDirectoryName(detail.label) &&
      normalizedDirectoryName(candidate.representedParty) ===
        normalizedDirectoryName(detail.representedParty),
    ) === index);
}

function normalizedDirectoryValue(kind, value) {
  const normalized = String(value || "").trim().toLocaleLowerCase("es");
  if (kind === "phone") return normalized.replace(/[^0-9+]/g, "");
  if (["email", "electronic_address"].includes(kind)) {
    return normalized.replace(/\s+/g, "");
  }
  return normalized.replace(/\s+/g, " ");
}

function normalizedDirectoryName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function directoryIdentityKeys(entry) {
  const keys = new Set();
  const entityType = entry.entityType || "other";
  const registration = String(entry.registration || "")
    .toLocaleLowerCase("es")
    .replace(/[^a-z0-9]/g, "");
  const documentNumber = String(entry.documentNumber || "")
    .toLocaleLowerCase("es")
    .replace(/[^a-z0-9]/g, "");
  const names = [entry.name, ...(entry.aliases || [])]
    .map(normalizedDirectoryName)
    .filter(Boolean);
  if (registration) keys.add(`registration:${entityType}:${registration}`);
  if (documentNumber) keys.add(`document:${documentNumber}`);
  names.forEach((name) => {
    const sortedName = name.split(" ").filter(Boolean).sort().join(" ");
    keys.add(`name:${entityType}:${name}`);
    keys.add(`name-sorted:${entityType}:${sortedName}`);
    if (["party", "organization"].includes(entityType)) {
      keys.add(`subject-name:${name}`);
      keys.add(`subject-name-sorted:${sortedName}`);
    }
  });
  (entry.contacts || []).forEach((contact) => {
    const value = normalizedDirectoryValue(contact.kind, contact.value);
    if (
      value.length >= 6 &&
      ["phone", "email", "electronic_address", "website"].includes(contact.kind)
    ) {
      keys.add(`contact:${contact.kind}:${value}`);
    }
  });
  return keys;
}

function mergeDirectoryEntries(prior, entry) {
  const contacts = new Map();
  [...(prior?.contacts || []), ...(entry.contacts || [])].forEach((contact) => {
    const key = `${contact.kind}:${normalizedDirectoryValue(contact.kind, contact.value)}`;
    const previous = contacts.get(key);
    contacts.set(key, {
      ...previous,
      ...contact,
      confidence: Math.max(
        Number(previous?.confidence || 0),
        Number(contact.confidence || 0),
      ),
      evidence: [...(previous?.evidence || []), ...(contact.evidence || [])]
        .filter((evidence, index, all) =>
          all.findIndex((candidate) =>
            candidate.documentSourceId === evidence.documentSourceId &&
            candidate.page === evidence.page &&
            candidate.excerpt === evidence.excerpt,
          ) === index),
    });
  });
  return {
    ...prior,
    ...entry,
    id: prior?.id || entry.id,
    entityType: entry.entityType || prior?.entityType || "other",
    name: entry.name || prior?.name || null,
    role: entry.role || prior?.role || null,
    representedParty: entry.representedParty || prior?.representedParty || null,
    registration: entry.registration || prior?.registration || null,
    documentType: entry.documentType || prior?.documentType || null,
    documentNumber: entry.documentNumber || prior?.documentNumber || null,
    roleDetails: directoryRoleDetails({
      roleDetails: [
        ...directoryRoleDetails(prior || {}),
        ...directoryRoleDetails(entry),
      ],
    }),
    hasRosterMatch: Boolean(prior?.hasRosterMatch || entry.hasRosterMatch),
    rosterMatchConfirmed: Boolean(
      prior?.rosterMatchConfirmed || entry.rosterMatchConfirmed,
    ),
    aliases: [...new Set([
      ...(prior?.aliases || []),
      ...(entry.aliases || []),
      prior?.name && prior.name !== entry.name ? prior.name : null,
    ].filter(Boolean))],
    contacts: [...contacts.values()],
    sourceIds: [...new Set([
      ...(prior?.sourceIds || []),
      ...(entry.sourceIds || []),
    ].filter(Boolean))],
    confidence: Math.max(
      Number(prior?.confidence || 0),
      Number(entry.confidence || 0),
    ),
    judicialDirectory:
      entry.judicialDirectory || prior?.judicialDirectory || null,
  };
}

function consolidateContactDirectory(entries) {
  const clusters = [];
  entries.forEach((entry) => {
    if (!entry.name && !(entry.contacts || []).length) return;
    const entryKeys = directoryIdentityKeys(entry);
    const matchingIndexes = clusters
      .map((cluster, index) =>
        [...entryKeys].some((key) => cluster.keys.has(key)) ? index : -1)
      .filter((index) => index >= 0);
    let combined = entry;
    const keys = new Set(entryKeys);
    matchingIndexes.forEach((index) => {
      combined = mergeDirectoryEntries(clusters[index].entry, combined);
      clusters[index].keys.forEach((key) => keys.add(key));
    });
    matchingIndexes.reverse().forEach((index) => clusters.splice(index, 1));
    directoryIdentityKeys(combined).forEach((key) => keys.add(key));
    clusters.push({ entry: combined, keys });
  });
  return clusters
    .map(({ entry }) => ({
      ...entry,
      roleDetails: directoryRoleDetails(entry),
      contacts: [...(entry.contacts || [])].sort((left, right) =>
        CONTACT_KIND_ORDER.indexOf(left.kind) - CONTACT_KIND_ORDER.indexOf(right.kind) ||
        String(left.value).localeCompare(String(right.value), "es")),
    }))
    .sort((left, right) =>
      String(left.name || "").localeCompare(String(right.name || ""), "es"));
}

function buildExpedienteDirectoryEntries({
  partyGroups,
  contactDirectory,
  actors,
  judge,
}) {
  const entries = [...(contactDirectory || [])];
  PARTY_GROUPS.forEach((group) => {
    (partyGroups[group.id] || []).forEach((party) => {
      const partyName = party.nombreApellido || null;
      const rosterMatches = [
        party.rosterMatch,
        ...(party.representantes || []).map(({ rosterMatch }) => rosterMatch),
      ].filter(Boolean);
      entries.push({
        id: `party:${party.partyKey}`,
        aliases: [],
        entityType: "party",
        name: partyName,
        role: group.tableLabel,
        representedParty: null,
        registration: null,
        documentType: party.tipoDocumento || null,
        documentNumber: party.numeroDocumento || null,
        contacts: [],
        sourceIds: ["pjm:partes"],
        confidence: party.groupEvidence?.source === "caption" ? 0.75 : 1,
        hasRosterMatch: Boolean(party.hasRosterMatch),
        rosterMatchConfirmed: rosterMatches.some((match) => !match.inferred),
      });
      (party.representantes || []).forEach((representative, index) => {
        entries.push({
          id: `representative:${party.partyKey}:${index}`,
          aliases: [],
          entityType: "lawyer",
          name: representative.nombreApellido || null,
          role: representative.vinculo || "Abogado/a",
          representedParty: partyName,
          registration:
            representative.matricula ||
            representative.numeroMatricula ||
            representative.matriculaProfesional ||
            representative.rosterMatch?.lawyer?.registration?.number ||
            null,
          documentType: representative.tipoDocumento || null,
          documentNumber: representative.numeroDocumento || null,
          contacts: [],
          sourceIds: ["pjm:partes"],
          confidence: 1,
          hasRosterMatch: Boolean(representative.rosterMatch),
          rosterMatchConfirmed: Boolean(
            representative.rosterMatch && !representative.rosterMatch.inferred,
          ),
        });
      });
    });
  });
  (actors || []).forEach((actor, index) => {
    if (!actor.name) return;
    entries.push({
      id: `actor:${actor.kind}:${index}`,
      aliases: [],
      entityType: actor.kind === "insurer" ? "organization" : actor.kind,
      name: actor.name,
      role: actor.role || CONTACT_ENTITY_LABELS[actor.kind] || "Interviniente",
      representedParty: actor.representedParty || null,
      registration: actor.registration || null,
      documentType: null,
      documentNumber: null,
      contacts: [],
      sourceIds: [],
      confidence: 1,
    });
  });
  if (judge) {
    entries.push({
      id: "expediente:judge",
      aliases: [],
      entityType: "judge",
      name: judge,
      role: "Juez/a",
      representedParty: null,
      registration: null,
      documentType: null,
      documentNumber: null,
      contacts: [],
      sourceIds: ["pjm:expediente"],
      confidence: 1,
    });
  }
  return consolidateContactDirectory(entries).map((entry) => {
    const roleDetails = directoryRoleDetails(entry);
    return {
      ...entry,
      roleDetails,
      group: directoryGroupFor({ ...entry, roleDetails }),
      historical:
        roleDetails.length > 0 && roleDetails.every(({ historical }) => historical),
    };
  });
}

function CaseMap({ partyGroups }) {
  const groups = PARTY_GROUPS.map((group) => ({
    ...group,
    parties: partyGroups[group.id] || [],
  })).filter(({ parties }) => parties.length);
  if (!groups.length) return null;

  return (
    <div className="expediente-case-map" aria-label="Mapa del caso">
      {groups.map((group) => (
        <article key={group.id}>
          <span>{group.tableLabel}</span>
          {group.parties.map((party) => (
            <div className="expediente-case-map-party" key={party.partyKey}>
              <strong>{party.nombreApellido || "Nombre no informado"}</strong>
              {party.representantes?.length ? (
                <small>
                  {party.representantes
                    .map(({ nombreApellido }) => nombreApellido)
                    .filter(Boolean)
                    .join(" · ")}
                </small>
              ) : (
                <small>Sin representante informado</small>
              )}
            </div>
          ))}
        </article>
      ))}
    </div>
  );
}

function directorySourceSummary(entry) {
  const sourceIds = [...new Set([
    ...(entry.sourceIds || []),
    ...(entry.contacts || []).flatMap((contact) =>
      (contact.evidence || []).map(({ documentSourceId }) => documentSourceId)),
  ].filter(Boolean))];
  const hasJudicialGuideSource = sourceIds.some((sourceId) =>
    String(sourceId).startsWith("pjm:guiajudicial:"));
  const hasPjmSource = sourceIds.some((sourceId) =>
    String(sourceId).startsWith("pjm:") &&
    !String(sourceId).startsWith("pjm:guiajudicial:"));
  const actuationCount = sourceIds.filter(
    (sourceId) => !String(sourceId).startsWith("pjm:"),
  ).length;
  return [
    hasJudicialGuideSource ? "Guía Judicial del PJM" : null,
    hasPjmSource ? "PJM" : null,
    actuationCount
      ? `${actuationCount} ${actuationCount === 1 ? "actuación" : "actuaciones"}`
      : null,
  ].filter(Boolean).join(" + ") || "Inteligencia del expediente";
}

function DirectoryContact({ contact, compact = false }) {
  const ContactIcon = CONTACT_KIND_ICONS[contact.kind] || FileText;
  const kindLabel = CONTACT_KIND_LABELS[contact.kind] || contact.label || "Contacto";
  const copiedMessages = {
    phone: "Teléfono copiado",
    address: "Domicilio copiado",
    electronic_address: "Domicilio electrónico copiado",
    email: "Correo copiado",
    website: "Enlace copiado",
  };
  return (
    <span
      className={`information-contact-value-line ${compact ? "is-compact" : ""}`}
      title={kindLabel}
    >
      <ContactIcon size={14} aria-hidden="true" />
      <CopyableText
        copiedMessage={copiedMessages[contact.kind] || `${kindLabel} copiado`}
        label={kindLabel.toLocaleLowerCase("es-AR")}
        value={contact.value}
      />
    </span>
  );
}

function DirectoryIdentityMeta({ entry }) {
  const documentType = String(entry.documentType || "Documento").trim();
  const normalizedDocumentType = documentType.toLocaleLowerCase("es-AR");
  const hasDni = normalizedDocumentType.includes("dni");
  const hasTaxId = normalizedDocumentType.includes("cuit")
    || normalizedDocumentType.includes("cuil");
  const documentLabel = hasDni && hasTaxId
    ? "Documento"
    : hasTaxId
      ? normalizedDocumentType.includes("cuil") ? "CUIL" : "CUIT"
      : hasDni
        ? "DNI"
        : documentType;
  const documentCopiedMessage = documentLabel === "CUIT"
    ? "CUIT copiado"
    : documentLabel === "CUIL"
      ? "CUIL copiado"
    : documentLabel === "DNI"
      ? "DNI copiado"
      : "Documento copiado";

  return (
    <>
      {entry.registration && (
        <CopyableText
          copiedMessage="Matrícula copiada"
          label="matrícula"
          value={entry.registration}
        >
          Matrícula {entry.registration}
        </CopyableText>
      )}
      {entry.registration && entry.documentNumber && " · "}
      {entry.documentNumber && (
        <CopyableText
          copiedMessage={documentCopiedMessage}
          label={documentLabel.toLocaleLowerCase("es-AR")}
          value={entry.documentNumber}
        >
          {documentLabel} {entry.documentNumber}
        </CopyableText>
      )}
    </>
  );
}

function ExpedienteDirectoryBlock({
  entries,
  partyGroups,
  loading,
}) {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const counts = Object.fromEntries(
    DIRECTORY_GROUPS.map(({ id }) => [
      id,
      entries.filter((entry) =>
        id === "previous"
          ? entry.historical
          : !entry.historical && (id === "all" || entry.group === id),
      ).length,
    ]),
  );
  const visibleEntries = entries
    .filter((entry) =>
      filter === "previous"
        ? entry.historical
        : !entry.historical && (filter === "all" || entry.group === filter))
    .filter((entry) =>
      matchesApproximateText([
        entry.name,
        entry.registration,
        entry.documentNumber,
        ...(entry.aliases || []),
        ...(entry.roleDetails || []).flatMap(({ label, representedParty }) => [
          label,
          representedParty,
        ]),
        ...(entry.contacts || []).map(({ value }) => value),
      ], query))
    .sort((left, right) =>
      DIRECTORY_GROUP_ORDER.indexOf(left.group) -
        DIRECTORY_GROUP_ORDER.indexOf(right.group) ||
      String(left.name || "").localeCompare(String(right.name || ""), "es"));
  const toggleExpanded = (entryId) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };

  return (
    <DataTableSection
      className="expediente-detail-table-section information-directory-section"
      description={`${entries.length} ${entries.length === 1 ? "interviniente identificado" : "intervinientes identificados"}`}
      icon={UsersRound}
      title="Directorio del expediente"
    >
      <CaseMap partyGroups={partyGroups} />
      {loading && entries.length > 0 && (
        <div
          className="information-directory-loading"
          role="status"
          aria-live="polite"
        >
          <LoaderCircle className="spin" size={15} aria-hidden="true" />
          <span>Completando partes, abogados y datos de contacto…</span>
        </div>
      )}
      <div className="information-directory-toolbar">
        <div className="information-directory-filters" role="group" aria-label="Filtrar directorio">
          {DIRECTORY_GROUPS.map(({ id, label }) => (
            <button
              className={filter === id ? "is-active" : ""}
              key={id}
              type="button"
              onClick={() => setFilter(id)}
            >
              {label} <span>{counts[id]}</span>
            </button>
          ))}
        </div>
        <label className="information-directory-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar nombre, DNI, matrícula o contacto"
          />
        </label>
      </div>
      {loading && !entries.length ? (
        <DetailsSectionLoading label="Cargando directorio" />
      ) : (
        <DataTable
          ariaLabel="Directorio de intervinientes del expediente"
          className="information-directory-table"
          density="compact"
          minWidth="760px"
        >
          <DataTableHead>
            <DataTableRow>
              <DataTableHeaderCell className="information-directory-person">
                Interviniente
              </DataTableHeaderCell>
              <DataTableHeaderCell
                className="information-directory-role"
                priority="secondary"
              >
                Función actual
              </DataTableHeaderCell>
              <DataTableHeaderCell
                className="information-directory-contact"
                priority="secondary"
              >
                Contacto principal
              </DataTableHeaderCell>
              <DataTableHeaderCell className="information-directory-action" align="end">
                <span className="sr-only">Detalle</span>
              </DataTableHeaderCell>
            </DataTableRow>
          </DataTableHead>
          <DataTableBody>
            {visibleEntries.length ? visibleEntries.map((entry, index) => {
              const entryId = entry.id || `${entry.name}:${index}`;
              const expanded = expandedIds.has(entryId);
              const activeRoles = entry.roleDetails.filter(({ historical }) => !historical);
              const visibleRole = (activeRoles[0] || entry.roleDetails[0])?.label ||
                CONTACT_ENTITY_LABELS[entry.entityType] || "Interviniente";
              const representedParty = (activeRoles[0] || entry.roleDetails[0])?.representedParty;
              const primaryContact = entry.contacts[0];
              const hiddenContactCount = Math.max(0, entry.contacts.length - 1);
              return (
                <Fragment key={entryId}>
                  <DataTableRow
                    className={entry.historical ? "is-directory-historical" : ""}
                    label={`${expanded ? "Ocultar" : "Ver"} detalle de ${entry.name}`}
                    onActivate={() => toggleExpanded(entryId)}
                  >
                    <DataTableCell className="information-directory-person">
                      <DataTablePrimaryCell
                        action={entry.name ? (
                          <CopyValueButton value={entry.name} label="nombre" />
                        ) : null}
                        title={entry.name || "Nombre no informado"}
                        meta={entry.registration || entry.documentNumber ? (
                          <DirectoryIdentityMeta entry={entry} />
                        ) : null}
                        badges={(
                          <>
                            {entry.hasRosterMatch && (
                              <DataTableBadge tone={entry.rosterMatchConfirmed ? "success" : "warning"}>
                                {entry.rosterMatchConfirmed ? "En nuestra nómina" : "Posible nómina"}
                              </DataTableBadge>
                            )}
                            {entry.judicialDirectory && (
                              <DataTableBadge>Guía Judicial</DataTableBadge>
                            )}
                          </>
                        )}
                      />
                    </DataTableCell>
                    <DataTableCell
                      className="information-directory-role"
                      priority="secondary"
                    >
                      <DataTableValue
                        primary={visibleRole}
                        secondary={representedParty ? `Representa a ${representedParty}` : null}
                      />
                    </DataTableCell>
                    <DataTableCell
                      className="information-directory-contact"
                      priority="secondary"
                    >
                      {primaryContact ? (
                        <span className="information-directory-primary-contact">
                          <DirectoryContact contact={primaryContact} compact />
                          {hiddenContactCount > 0 && <small>+{hiddenContactCount} datos</small>}
                        </span>
                      ) : (
                        <span className="information-directory-empty-contact">
                          Sin datos de contacto
                        </span>
                      )}
                    </DataTableCell>
                    <DataTableCell className="information-directory-action" align="end">
                      <DataTableAction
                        label={`${expanded ? "Ocultar" : "Ver"} detalle de ${entry.name}`}
                        icon={expanded
                          ? <ChevronDown size={16} aria-hidden="true" />
                          : <ChevronRight size={16} aria-hidden="true" />}
                        onClick={() => toggleExpanded(entryId)}
                      />
                    </DataTableCell>
                  </DataTableRow>
                  {expanded && (
                    <DataTableRow className="information-directory-detail-row">
                      <DataTableCell colSpan={4}>
                        <div className="information-directory-detail">
                          <div>
                            <strong>Contactos</strong>
                            {entry.contacts.length ? (
                              <div className="information-contact-list">
                                {entry.contacts.map((contact, contactIndex) => (
                                  <DirectoryContact
                                    contact={contact}
                                    key={`${contact.kind}:${normalizedDirectoryValue(contact.kind, contact.value)}:${contactIndex}`}
                                  />
                                ))}
                              </div>
                            ) : <span>Sin datos informados</span>}
                          </div>
                          <div>
                            <strong>Funciones</strong>
                            <div className="information-directory-roles">
                              {entry.roleDetails.map((detail, roleIndex) => (
                                <span key={`${detail.label}:${roleIndex}`}>
                                  {detail.label}
                                  {detail.historical && <DataTableBadge>Anterior</DataTableBadge>}
                                  {detail.representedParty && <small>Representa a {detail.representedParty}</small>}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <strong>Origen</strong>
                            <span>{directorySourceSummary(entry)}</span>
                            {entry.judicialDirectory && (
                              <>
                                {entry.judicialDirectory.office && (
                                  <small>{entry.judicialDirectory.office}</small>
                                )}
                                {[entry.judicialDirectory.jurisdiction,
                                  entry.judicialDirectory.district]
                                  .filter(Boolean).length > 0 && (
                                  <small>
                                    {[entry.judicialDirectory.jurisdiction,
                                      entry.judicialDirectory.district]
                                      .filter(Boolean).join(" · ")}
                                  </small>
                                )}
                              </>
                            )}
                            {Number.isFinite(entry.confidence) && (
                              <small>Confianza {Math.round(entry.confidence * 100)}%</small>
                            )}
                          </div>
                        </div>
                      </DataTableCell>
                    </DataTableRow>
                  )}
                </Fragment>
              );
            }) : (
              <DataTableRow>
                <DataTableCell colSpan={4}>
                  <span className="information-directory-no-results">
                    {filter === "witness" && !query
                      ? "No se identificaron testigos en la inteligencia disponible."
                      : "No hay intervinientes que coincidan con este filtro."}
                  </span>
                </DataTableCell>
              </DataTableRow>
            )}
          </DataTableBody>
        </DataTable>
      )}
    </DataTableSection>
  );
}

function RelatedExpedientesTable({ items, onOpen }) {
  return (
    <DataTable
      ariaLabel="Expedientes relacionados"
      className="information-related-table"
      density="compact"
      minWidth="520px"
    >
      <DataTableHead>
        <DataTableRow>
          <DataTableHeaderCell className="information-related-record">
            Expediente
          </DataTableHeaderCell>
          <DataTableHeaderCell
            className="information-related-kind"
            priority="secondary"
          >
            Relación
          </DataTableHeaderCell>
          <DataTableHeaderCell
            className="information-related-action"
            align="end"
          >
            <span className="sr-only">Acciones</span>
          </DataTableHeaderCell>
        </DataTableRow>
      </DataTableHead>
      <DataTableBody>
        {items.map((related, index) => {
          const relation = related.relationTypes.length
            ? related.relationTypes.join(" · ")
            : "Relación no informada";
          const open = related.cuij ? () => onOpen(related.cuij) : null;
          return (
            <DataTableRow
              key={related.preExpId || `${related.cuij || "related"}-${index}`}
              label={related.cuij ? `Abrir expediente ${related.cuij}` : null}
              onActivate={open}
            >
              <DataTableCell className="information-related-record">
                <DataTablePrimaryCell
                  eyebrow={related.cuij || "Sin CUIJ"}
                  title={related.caratula || "Sin carátula informada"}
                />
              </DataTableCell>
              <DataTableCell priority="secondary">
                <DataTableValue primary={relation} />
              </DataTableCell>
              <DataTableCell align="end">
                {open && (
                  <DataTableAction
                    label={`Abrir expediente ${related.cuij}`}
                    icon={<ChevronRight size={16} aria-hidden="true" />}
                    onClick={open}
                  />
                )}
              </DataTableCell>
            </DataTableRow>
          );
        })}
      </DataTableBody>
    </DataTable>
  );
}

function DetailsSectionLoading({
  label,
  detail = "Consultando el Poder Judicial…",
}) {
  return (
    <div className="expediente-section-loading" aria-live="polite">
      <LoaderCircle className="spin" size={18} />
      <div>
        <strong>{label}</strong>
        <span>{detail}</span>
      </div>
    </div>
  );
}

function SentenceDocumentsBlock({
  documents,
  documentLoading,
  relatedLoading,
  relatedError,
  onOpenDocument,
  title = "Sentencias",
  singularDescription = "sentencia identificada",
  pluralDescription = "sentencias identificadas",
  ariaLabel = "Sentencias del expediente y sus relacionados",
  emptyMessage = "No se encontraron sentencias en este expediente ni en sus relacionados.",
}) {
  const sourceCount = new Set(
    documents.map(({ sourceCuij }) => normalizeCuij(sourceCuij)).filter(Boolean),
  ).size;
  const sourceDescription = sourceCount
    ? ` en ${sourceCount} ${sourceCount === 1 ? "expediente" : "expedientes"}`
    : "";
  return (
    <DataTableSection
      className="expediente-detail-table-section documents-table-section"
      description={`${documents.length} ${
        documents.length === 1 ? singularDescription : pluralDescription
      }${sourceDescription}`}
      icon={Landmark}
      title={title}
    >
      <DataTable
        ariaLabel={ariaLabel}
        className="documents-table sentence-documents-table"
        density="compact"
        minWidth="760px"
      >
        <DataTableHead>
          <DataTableRow>
            <DataTableHeaderCell className="documents-table-document">
              Documento
            </DataTableHeaderCell>
            <DataTableHeaderCell className="documents-table-source">
              Expediente
            </DataTableHeaderCell>
            <DataTableHeaderCell className="documents-table-date">
              Fecha
            </DataTableHeaderCell>
            <DataTableHeaderCell
              className="documents-table-file"
              align="end"
            >
              Archivo
            </DataTableHeaderCell>
          </DataTableRow>
        </DataTableHead>
        <DataTableBody>
          {documents.map(({
            milestoneId,
            instance,
            label,
            actuacion,
            sourceCuij,
            relationTypes,
            isRelated,
          }) => {
            const documentId = actuacionDocumentId(actuacion);
            const isLoading = Boolean(documentId) && documentLoading === documentId;
            const title = actuacion.titulo || label;
            const canOpen = Boolean(documentId) &&
              actuacion.documentOpenable !== false;
            return (
              <DataTableRow key={`${milestoneId}:${documentId}`}>
                <DataTableCell className="documents-table-document">
                  <DataTablePrimaryCell
                    eyebrow={instance}
                    title={title}
                    meta={actuacion.numero ? `N.º ${actuacion.numero}` : null}
                  />
                </DataTableCell>
                <DataTableCell priority="secondary">
                  <DataTableValue
                    primary={sourceCuij || "Sin CUIJ"}
                    secondary={isRelated
                      ? relationTypes?.join(" · ") || "Relacionado"
                      : "Expediente actual"}
                  />
                </DataTableCell>
                <DataTableCell>
                  <DataTableValue
                    primary={(
                      <RenderedTimelineDate
                        dateOnly={actuacion.dateOnly}
                        value={actuacion.fechaFirma || actuacion.fechaPub}
                      />
                    )}
                  />
                </DataTableCell>
                <DataTableCell align="end">
                  <DataTableAction
                    disabled={Boolean(documentLoading) || !canOpen}
                    label={canOpen
                      ? `Ver PDF de ${title}`
                      : `El PDF de ${title} está disponible en la fuente`}
                    icon={
                      isLoading ? (
                        <LoaderCircle className="spin" size={15} />
                      ) : (
                        <Eye size={15} />
                      )
                    }
                    onClick={() => onOpenDocument(actuacion, sourceCuij)}
                  >
                    {isLoading ? "Buscando" : canOpen ? "PDF" : "En la fuente"}
                  </DataTableAction>
                </DataTableCell>
              </DataTableRow>
            );
          })}
          {relatedLoading && (
            <DataTableRow>
              <DataTableCell colSpan={4}>
                <span className="documents-table-loading">
                  <LoaderCircle className="spin" size={14} />
                  Buscando sentencias en expedientes relacionados…
                </span>
              </DataTableCell>
            </DataTableRow>
          )}
          {relatedError && !relatedLoading && (
            <DataTableRow>
              <DataTableCell colSpan={4}>
                <span className="documents-table-warning">{relatedError}</span>
              </DataTableCell>
            </DataTableRow>
          )}
          {!documents.length && !relatedLoading && !relatedError && (
            <DataTableRow>
              <DataTableCell colSpan={4}>
                <span className="documents-table-empty">
                  {emptyMessage}
                </span>
              </DataTableCell>
            </DataTableRow>
          )}
        </DataTableBody>
      </DataTable>
    </DataTableSection>
  );
}

function ExpertReportsBlock({
  reports,
  documentLoading,
  attachmentLoading,
  onOpenDocument,
}) {
  return (
    <DataTableSection
      className="expediente-detail-table-section documents-table-section"
      description={`${reports.length} ${
        reports.length === 1 ? "informe identificado" : "informes identificados"
      }`}
      icon={UsersRound}
      title="Informes periciales"
    >
      <DataTable
        ariaLabel="Informes periciales del expediente"
        className="documents-table expert-reports-table"
        density="compact"
        minWidth="680px"
      >
        <DataTableHead>
          <DataTableRow>
            <DataTableHeaderCell className="documents-table-document">
              Documento
            </DataTableHeaderCell>
            <DataTableHeaderCell className="documents-table-expert">
              Perito
            </DataTableHeaderCell>
            <DataTableHeaderCell className="documents-table-date">
              Fecha
            </DataTableHeaderCell>
            <DataTableHeaderCell
              className="documents-table-file"
              align="end"
            >
              Archivo
            </DataTableHeaderCell>
          </DataTableRow>
        </DataTableHead>
        <DataTableBody>
          {reports.length ? (
            reports.map((report) => {
              const documentId = actuacionDocumentId(report.actuacion);
              const isLoading = report.attachmentId
                ? attachmentLoading === String(report.attachmentId)
                : Boolean(documentId) && documentLoading === documentId;
              const title = report.actuacion.titulo || "Informe pericial";
              return (
                <DataTableRow key={report.key}>
                  <DataTableCell className="documents-table-document">
                    <DataTablePrimaryCell title={title} />
                  </DataTableCell>
                  <DataTableCell>
                    <DataTableValue
                      primary={report.expertName || "Perito no identificado"}
                      secondary={
                        report.registration
                          ? (
                            <CopyableText
                              copiedMessage="Matrícula copiada"
                              label="matrícula"
                              value={report.registration}
                            >
                              Matrícula {report.registration}
                            </CopyableText>
                          )
                          : null
                      }
                    />
                  </DataTableCell>
                  <DataTableCell>
                    <DataTableValue
                      primary={(
                        <RenderedTimelineDate
                          value={
                            report.submittedAt ||
                            report.actuacion.fechaFirma ||
                            report.actuacion.fechaPub
                          }
                        />
                      )}
                    />
                  </DataTableCell>
                  <DataTableCell align="end">
                    <DataTableAction
                      disabled={Boolean(documentLoading)}
                      label={`Ver PDF de ${title}`}
                      icon={
                        isLoading ? (
                          <LoaderCircle className="spin" size={15} />
                        ) : (
                          <Eye size={15} />
                        )
                      }
                      onClick={() => onOpenDocument(report.actuacion)}
                    >
                      {isLoading ? "Buscando" : "PDF"}
                    </DataTableAction>
                  </DataTableCell>
                </DataTableRow>
              );
            })
          ) : (
            <DataTableRow>
              <DataTableCell colSpan={4}>
                <span className="documents-table-empty">
                  No se encontraron informes periciales.
                </span>
              </DataTableCell>
            </DataTableRow>
          )}
        </DataTableBody>
      </DataTable>
    </DataTableSection>
  );
}

function UploadedDocumentsBlock() {
  return (
    <DataTableSection
      className="expediente-detail-table-section documents-table-section"
      description="Archivos incorporados por el estudio"
      icon={Paperclip}
      title="Documentos subidos"
    >
      <DataTable
        ariaLabel="Documentos subidos por el usuario"
        className="documents-table uploaded-documents-table"
        density="compact"
        minWidth="560px"
      >
        <DataTableHead>
          <DataTableRow>
            <DataTableHeaderCell className="documents-table-document">
              Documento
            </DataTableHeaderCell>
            <DataTableHeaderCell className="documents-table-date">
              Fecha
            </DataTableHeaderCell>
            <DataTableHeaderCell
              className="documents-table-file"
              align="end"
            >
              Archivo
            </DataTableHeaderCell>
          </DataTableRow>
        </DataTableHead>
        <DataTableBody>
          <DataTableRow>
            <DataTableCell colSpan={3}>
              <span className="documents-table-empty">
                Todavía no hay documentos subidos por el usuario.
              </span>
            </DataTableCell>
          </DataTableRow>
        </DataTableBody>
      </DataTable>
    </DataTableSection>
  );
}

function ActuacionesTable({
  items,
  documentLoading,
  loadingActId,
  onOpenDocument,
  inconsistencies = [],
  onNavigateEvidence,
  highlightedEntryId,
  highlightPatterns = [],
}) {
  return (
    <DataTable
      ariaLabel="Actuaciones y movimientos del expediente"
      className="actuaciones-table"
      density="compact"
      minWidth="720px"
    >
      <DataTableHead>
        <DataTableRow>
          <DataTableHeaderCell className="actuaciones-table-record">
            Registro
          </DataTableHeaderCell>
          <DataTableHeaderCell
            className="actuaciones-table-date"
            priority="secondary"
          >
            Fecha
          </DataTableHeaderCell>
          <DataTableHeaderCell
            className="actuaciones-table-source"
            priority="tertiary"
          >
            Origen / firmantes
          </DataTableHeaderCell>
          <DataTableHeaderCell
            className="actuaciones-table-document"
            align="end"
          >
            PDF
          </DataTableHeaderCell>
        </DataTableRow>
      </DataTableHead>
      <DataTableBody>
        {items.map((item) => {
          const targeted = highlightedEntryId === item.entryId;

          if (item.entryType === "movimiento") {
            const route = [item.origen, item.destino]
              .filter(Boolean)
              .join(" → ");
            return (
              <DataTableRow
                className={`timeline-kind-movimientos ${
                  targeted ? "is-targeted" : ""
                }`}
                id={timelineDomId(item)}
                key={item.entryId}
                tabIndex={-1}
              >
                <DataTableCell className="actuaciones-table-record">
                  <DataTablePrimaryCell
                    eyebrow="Movimiento"
                    title={(
                      <ApproximateMatchText
                        value={item.descripcion || "Movimiento"}
                        patterns={highlightPatterns}
                      />
                    )}
                  />
                </DataTableCell>
                <DataTableCell priority="secondary">
                  <DataTableValue
                    primary={<RenderedTimelineDate value={item.occurredAt} />}
                  />
                </DataTableCell>
                <DataTableCell priority="tertiary">
                  <DataTableValue
                    primary={route || "Sin origen informado"}
                    secondary={route ? "Origen y destino" : null}
                  />
                </DataTableCell>
                <DataTableCell align="end">
                  <span className="actuaciones-table-no-document">—</span>
                </DataTableCell>
              </DataTableRow>
            );
          }

          const actuacion = item;
          const documentId = actuacionDocumentId(actuacion);
          const canOpenDocument = Boolean(documentId) &&
            actuacion.documentOpenable !== false;
          const isLoading = Boolean(documentId) && loadingActId === documentId;
          const inconsistency = inconsistencies.find(
            (issue) =>
              String(issue.primarySourceId || "") === String(documentId || ""),
          );
          const status = actuacion.category === "escritos"
            ? "Escrito Público"
            : actuacion.estadoNotificacion || "Sin estado";
          const recordDetails = [
            actuacion.tipo,
            status,
          ].filter(Boolean).join(" · ");
          const source = actuacion.organismoCreador || actuacion.firmantes;
          const sourceSecondary = actuacion.organismoCreador && actuacion.firmantes
            ? actuacion.firmantes
            : null;

          return (
            <Fragment key={actuacion.entryId}>
              <DataTableRow
                className={`timeline-kind-${actuacion.category} ${
                  targeted ? "is-targeted" : ""
                } ${inconsistency ? "has-issue" : ""}`}
                id={timelineDomId(actuacion)}
                tabIndex={-1}
              >
                <DataTableCell className="actuaciones-table-record">
                  <DataTablePrimaryCell
                    eyebrow={(
                      <>
                        <span className="actuaciones-table-record-type">
                          {getActuacionCategory(actuacion)}
                        </span>
                        {recordDetails && (
                          <span className="actuaciones-table-record-details">
                            <ApproximateMatchText
                              value={recordDetails}
                              patterns={highlightPatterns}
                            />
                          </span>
                        )}
                      </>
                    )}
                    title={(
                      <ApproximateMatchText
                        value={actuacion.titulo || "Sin título informado"}
                        patterns={highlightPatterns}
                      />
                    )}
                    badges={(
                      <>
                        {actuacion.poseeAdjunto && (
                          <DataTableBadge
                            tone="warning"
                            icon={<Paperclip size={9} aria-hidden="true" />}
                          >
                            Adjuntos
                          </DataTableBadge>
                        )}
                        {inconsistency && (
                          <DataTableBadge
                            tone="danger"
                            icon={<CircleAlert size={9} aria-hidden="true" />}
                          >
                            Revisar
                          </DataTableBadge>
                        )}
                      </>
                    )}
                  />
                </DataTableCell>
                <DataTableCell priority="secondary">
                  <DataTableValue
                    primary={(
                      <RenderedTimelineDate
                        dateOnly={actuacion.dateOnly}
                        value={actuacion.fechaFirma || actuacion.fechaPub}
                      />
                    )}
                  />
                </DataTableCell>
                <DataTableCell priority="tertiary">
                  <DataTableValue
                    primary={source || "Sin informar"}
                    secondary={sourceSecondary}
                  />
                </DataTableCell>
                <DataTableCell align="end">
                  <DataTableAction
                    disabled={documentLoading || !canOpenDocument}
                    label={canOpenDocument
                      ? `Ver PDF de ${actuacion.titulo || "la actuación"}`
                      : actuacion.documentAvailable
                        ? "PDF disponible en la fuente"
                        : "Documento no disponible"}
                    icon={isLoading
                      ? <LoaderCircle className="spin" size={14} />
                      : <Eye size={14} />}
                    onClick={() => onOpenDocument(actuacion)}
                  >
                    {isLoading
                      ? "Buscando…"
                      : canOpenDocument
                        ? "PDF"
                        : actuacion.documentAvailable
                          ? "En la fuente"
                          : "—"}
                  </DataTableAction>
                </DataTableCell>
              </DataTableRow>
              {inconsistency && (
                <DataTableRow className="actuaciones-table-issue-row">
                  <DataTableCell colSpan={4}>
                    <div className="actuaciones-table-issue">
                      <CircleAlert size={15} aria-hidden="true" />
                      <div>
                        <strong>{inconsistency.title}</strong>
                        <span>{inconsistency.description}</span>
                      </div>
                      {inconsistency.data?.orderSourceId && (
                        <button
                          type="button"
                          onClick={() =>
                            onNavigateEvidence?.(
                              inconsistency.data.orderSourceId,
                            )
                          }
                        >
                          <Eye size={13} /> Ver decreto
                        </button>
                      )}
                      <WhatsappNoticeCopyButton
                        notice={inconsistency.whatsappNotice}
                        compact
                      />
                    </div>
                  </DataTableCell>
                </DataTableRow>
              )}
            </Fragment>
          );
        })}
      </DataTableBody>
    </DataTable>
  );
}

function deadlineStatusCopy(deadline) {
  if (deadline?.manualOverride?.status === "completed") {
    return "Cumplido manualmente";
  }
  if (deadline?.manualOverride?.status === "pending") {
    return "Pendiente manualmente";
  }
  if (deadline?.ruleId?.endsWith(".caducidad_instancia")) {
    return {
      upcoming: "En seguimiento",
      due_today: "Controlar hoy",
      overdue: "Control inmediato",
    }[deadline?.status] || "Por revisar";
  }
  if (
    deadline?.status === "expired_unexercised" &&
    deadline?.ruleId?.endsWith(".recurso_extraordinario_provincial")
  ) {
    return "Venció sin recurso detectado";
  }
  if (
    deadline?.status === "expired_unexercised" &&
    deadline?.ruleId?.endsWith(".aclaratoria_sentencia")
  ) {
    return "Venció sin aclaratoria detectada";
  }
  const resolvedCopy = {
    completed: "Cumplido",
    completed_inferred: "Cumplimiento inferido",
    closed_by_progress: "Cerrado por avance",
    expired_unexercised: "Vencido sin ejercicio",
    replaced: "Reemplazado",
    historical_reference: "Antecedente histórico",
    not_applicable: "No aplicable",
  }[deadline?.status];
  if (resolvedCopy) return resolvedCopy;
  if (["pending", "pending_conditions"].includes(deadline?.clockStatus)) {
    return "Pendiente de inicio";
  }
  if (deadline?.clockStatus === "suspended_or_conditioned") {
    return "Condicionado por una dependencia";
  }
  return {
    upcoming: "Próximo",
    due_today: "Vence hoy",
    overdue: "Vencido aparentemente",
    completed: "Cumplido",
    completed_inferred: "Cumplimiento inferido",
    closed_by_progress: "Cerrado por avance",
    expired_unexercised: "Vencido sin ejercicio",
    replaced: "Reemplazado",
    historical_reference: "Antecedente histórico",
    pending_conditions: "Pendiente de inicio",
    suspended_or_conditioned: "Vencimiento no confiable",
    needs_review: "Requiere revisión",
    not_applicable: "No aplicable",
    candidate: "Por confirmar",
    action_available: "Acción disponible",
  }[deadline?.status] || "Por revisar";
}

function deadlineEvidenceSourceId(deadline) {
  const evidence = deadline?.trigger?.evidence || [];
  return evidence.find(({ sourceId, actId, mexpId }) =>
    Boolean(sourceId || actId || mexpId),
  );
}

function deadlineClosureEvidenceSourceId(deadline) {
  const evidence = deadline?.closure?.evidence || [];
  return evidence.find(({ sourceId, actId, mexpId }) =>
    Boolean(sourceId || actId || mexpId),
  );
}

const RESOLVED_DEADLINE_STATUSES = new Set([
  "completed",
  "completed_inferred",
  "closed_by_progress",
  "expired_unexercised",
  "replaced",
  "historical_reference",
  "not_applicable",
]);

function deadlineDatePresentation(deadline) {
  if (["completed", "completed_inferred"].includes(deadline.status)) {
    return {
      label: "Cumplido",
      value:
        deadline.unit === "hours" && deadline.dueAt
          ? formatDate(deadline.dueAt, true)
          : formatDateOnly(
              deadline.status === "completed"
                ? deadline.completionDate
                : deadline.closure?.date,
            ),
      rawValue:
        deadline.unit === "hours" && deadline.dueAt
          ? deadline.dueAt
          : deadline.status === "completed"
            ? deadline.completionDate
            : deadline.closure?.date,
    };
  }
  if (RESOLVED_DEADLINE_STATUSES.has(deadline.status)) {
    return {
      label: "Cerrado",
      value: formatDateOnly(deadline.closure?.date),
      rawValue: deadline.closure?.date,
    };
  }
  if (["pending", "pending_conditions"].includes(deadline.clockStatus)) {
    return { label: "Inicio condicionado", value: "Aún no comenzó" };
  }
  if (deadline.clockStatus === "suspended_or_conditioned") {
    return {
      label: "Vencimiento condicionado",
      value: "Fecha original no confiable",
    };
  }
  if (deadline.actionNotice) {
    return {
      label: "Disponible desde",
      value: formatDateOnly(deadline.startDate),
      rawValue: deadline.startDate,
    };
  }
  return {
    label:
      deadline.unit === "calendar_months"
        ? "Fecha de control"
        : "Vencimiento estimado",
    value:
      deadline.unit === "hours" && deadline.dueAt
        ? formatDate(deadline.dueAt, true)
        : formatDateOnly(deadline.dueDate),
    rawValue: deadline.unit === "hours" && deadline.dueAt
      ? deadline.dueAt
      : deadline.dueDate,
  };
}

function deadlineDurationLabel(deadline) {
  return `${deadline.duration} ${
    deadline.unit === "hours"
      ? "horas"
      : deadline.unit === "calendar_months"
        ? deadline.duration === 1
          ? "mes calendario"
          : "meses calendario"
        : deadline.unit === "calendar_days"
          ? "días corridos"
          : "días hábiles"
  }`;
}

function DeadlineAttentionTable({
  items,
  selectedDeadlineId,
  onNavigateEvidence,
  representedPartyGroups,
  canOverride,
  overrideSaving,
  onSetOverride,
  manualReminderStatusSaving,
  manualReminderDeleting,
  onSetManualReminderStatus,
  onDeleteManualReminder,
  onOpenGenerator,
}) {
  const [activeDraft, setActiveDraft] = useState(null);
  const closeDraft = useCallback(() => setActiveDraft(null), []);

  return (
    <>
      <DataTable
        ariaLabel="Recordatorios del expediente"
        className="deadline-attention-table"
        density="compact"
        minWidth="1100px"
      >
      <DataTableHead>
        <DataTableRow>
          <DataTableHeaderCell className="deadline-table-title">
            Recordatorio
          </DataTableHeaderCell>
          <DataTableHeaderCell
            className="deadline-table-party"
            priority="secondary"
          >
            Responsable
          </DataTableHeaderCell>
          <DataTableHeaderCell className="deadline-table-start-date">
            Fecha inicio
          </DataTableHeaderCell>
          <DataTableHeaderCell className="deadline-table-due-date">
            Fecha vencimiento
          </DataTableHeaderCell>
          <DataTableHeaderCell
            className="deadline-table-actions-column"
            align="end"
          >
            Acciones
          </DataTableHeaderCell>
        </DataTableRow>
      </DataTableHead>
      <DataTableBody>
        {items.map((attentionItem) => {
          if (attentionItem.type === "manual") {
            const reminder = attentionItem.value;
            const completed =
              reminder.status === "completed" || Boolean(reminder.completedAt);
            const statusLabel = completed
              ? "Cumplido"
              : attentionItem.tone === "danger"
                ? "Requiere atención"
                : attentionItem.tone === "warning"
                  ? "Próximo"
                  : "Programado";
            const statusSaving = manualReminderStatusSaving === reminder.id;
            const deleting = manualReminderDeleting === reminder.id;
            return (
              <DataTableRow
                className={`deadline-table-main-row is-manual is-tone-${attentionItem.tone}`}
                key={attentionItem.id}
              >
                <DataTableCell className="deadline-table-title">
                  <div className="deadline-table-primary">
                    <DataTablePrimaryCell title={reminder.description} />
                  </div>
                </DataTableCell>
                <DataTableCell
                  className="deadline-table-party-cell"
                  label="Responsable"
                  priority="secondary"
                >
                  <DataTableValue primary="Usuario" />
                </DataTableCell>
                <DataTableCell
                  className="deadline-table-start-cell"
                  label="Fecha inicio"
                >
                  <DataTableValue primary="—" />
                </DataTableCell>
                <DataTableCell
                  className="deadline-table-due-cell"
                  label="Fecha vencimiento"
                >
                  <div className="deadline-table-date-status">
                    <DataTableValue
                      primary={<RenderedDateOnly value={reminder.dueDate} />}
                    />
                    <DataTableStatus
                      label={statusLabel}
                      description={
                        completed && reminder.completedAt
                          ? (
                            <>
                              Cumplido el{" "}
                              <RenderedDate
                                value={reminder.completedAt}
                                withTime
                              />
                            </>
                          )
                          : null
                      }
                      tone={attentionItem.tone}
                    />
                  </div>
                </DataTableCell>
                <DataTableCell
                  align="end"
                  className="deadline-table-actions-cell"
                  label="Acciones"
                >
                  <div className="deadline-table-actions">
                    <DataTableAction
                      disabled={statusSaving || deleting}
                      label={
                        completed
                          ? "Marcar recordatorio manual como pendiente"
                          : "Marcar recordatorio manual como cumplido"
                      }
                      icon={
                        statusSaving ? (
                          <LoaderCircle className="spin" size={14} />
                        ) : completed ? (
                          <CircleDot size={14} />
                        ) : (
                          <Check size={14} />
                        )
                      }
                      onClick={() =>
                        onSetManualReminderStatus(
                          reminder.id,
                          completed ? "pending" : "completed",
                        )
                      }
                    >
                      {completed ? "Marcar pendiente" : "Marcar cumplido"}
                    </DataTableAction>
                    <DataTableAction
                      disabled={statusSaving || deleting}
                      label="Eliminar recordatorio manual"
                      icon={
                        deleting ? (
                          <LoaderCircle className="spin" size={14} />
                        ) : (
                          <Trash2 size={14} />
                        )
                      }
                      onClick={() => onDeleteManualReminder(reminder)}
                    >
                      Eliminar
                    </DataTableAction>
                  </div>
                </DataTableCell>
              </DataTableRow>
            );
          }

          const deadline = attentionItem.value;
          const tone = trafficColorToTone(
            deadlineTrafficColor(deadline, representedPartyGroups),
          );
          const evidence = deadlineEvidenceSourceId(deadline);
          const evidenceId =
            evidence?.sourceId || evidence?.actId || evidence?.mexpId;
          const closureEvidence = deadlineClosureEvidenceSourceId(deadline);
          const closureEvidenceId =
            closureEvidence?.sourceId ||
            closureEvidence?.actId ||
            closureEvidence?.mexpId;
          const resolved = RESOLVED_DEADLINE_STATUSES.has(deadline.status);
          const draftAvailable =
            deadline.draftAction &&
            ["action_available", "upcoming", "due_today", "overdue"].includes(
              deadline.status,
            );
          const saving = overrideSaving?.deadlineId === deadline.id;
          const date = deadlineDatePresentation(deadline);
          const conditioned = [
            "pending",
            "pending_conditions",
            "suspended_or_conditioned",
          ].includes(deadline.clockStatus);
          const startValue = conditioned
            ? deadline.pendingConditions?.join(" · ") ||
              "Confirmar el hecho que inicia el cómputo"
            : <RenderedDateOnly value={deadline.startDate} />;

          return (
            <DataTableRow
              id={`procedure-deadline-${deadline.id}`}
              className={`deadline-table-main-row is-${deadline.status} is-tone-${tone} ${
                selectedDeadlineId === deadline.id ? "is-selected" : ""
              }`}
              key={deadline.id}
            >
              <DataTableCell className="deadline-table-title">
                <div className="deadline-table-primary">
                  <DataTablePrimaryCell
                    title={deadline.label}
                    badges={
                      deadline.manualOverride ? (
                        <DataTableBadge tone="warning">Editado</DataTableBadge>
                      ) : null
                    }
                  />
                  <div className="deadline-table-tags">
                    <DataTableBadge>
                      {deadline.actionNotice
                        ? "Sin vencimiento automático"
                        : `Duración legal: ${deadlineDurationLabel(deadline)}`}
                    </DataTableBadge>
                    <DataTableBadge>
                      Confianza {Math.round((deadline.confidence || 0) * 100)}%
                    </DataTableBadge>
                    <a
                      className="deadline-table-tag-link"
                      href={deadline.legalBasis.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {deadline.legalBasis.citation}
                      <ExternalLink size={10} />
                    </a>
                  </div>
                  {deadline.closure?.reason &&
                    deadline.closure.reason !== "explicit_completion" && (
                      <span className="deadline-table-closure">
                        <CheckCircle2 size={12} />
                        {deadline.closure.label ||
                          "Una actuación posterior demuestra que este punto procesal fue superado."}
                      </span>
                    )}
                  {deadline.status === "overdue" && deadline.overdueAction && (
                    <span className="deadline-table-overdue-action">
                      <CircleAlert size={12} />
                      Acción sugerida: {deadline.overdueAction}
                    </span>
                  )}
                  {deadline.status === "action_available" &&
                    deadline.actionPrompt && (
                      <span className="deadline-table-action-prompt">
                        <CircleAlert size={12} />
                        Acción sugerida: {deadline.actionPrompt}
                      </span>
                    )}
                </div>
              </DataTableCell>
              <DataTableCell
                className="deadline-table-party-cell"
                label="Responsable"
                priority="secondary"
              >
                <DataTableValue primary={deadline.duePartyLabel} />
              </DataTableCell>
              <DataTableCell
                className="deadline-table-start-cell"
                label="Fecha inicio"
              >
                <DataTableValue
                  primary={startValue}
                  secondary={deadline.trigger.label}
                />
              </DataTableCell>
              <DataTableCell
                className="deadline-table-due-cell"
                label="Fecha vencimiento"
              >
                <div className="deadline-table-date-status">
                  <DataTableValue
                    primary={date.rawValue ? (
                      <CopyableDate value={date.rawValue}>{date.value}</CopyableDate>
                    ) : date.value}
                    secondary={date.label}
                  />
                  <DataTableStatus
                    label={deadlineStatusCopy(deadline)}
                    tone={tone}
                  />
                </div>
              </DataTableCell>
              <DataTableCell
                align="end"
                className="deadline-table-actions-cell"
                label="Acciones"
              >
                <div className="deadline-table-actions">
                  {evidenceId && (
                    <DataTableAction
                      label="Ver actuación inicial"
                      icon={<Eye size={13} />}
                      onClick={() => onNavigateEvidence(evidenceId)}
                    >
                      Actuación inicial
                    </DataTableAction>
                  )}
                  {closureEvidenceId && closureEvidenceId !== evidenceId && (
                    <DataTableAction
                      label="Ver actuación de cierre"
                      icon={<Eye size={13} />}
                      onClick={() => onNavigateEvidence(closureEvidenceId)}
                    >
                      Actuación de cierre
                    </DataTableAction>
                  )}
                  {draftAvailable && (
                    <DataTableAction
                      label={deadline.draftAction.label}
                      icon={
                        deadline.draftAction.kind === "filing" ? (
                          <FileText size={13} />
                        ) : (
                          <MessageCircle size={13} />
                        )
                      }
                      onClick={() => setActiveDraft({
                        ...deadline.draftAction,
                        deadlineId: deadline.id,
                        reminderLabel: deadline.label,
                      })}
                    >
                      {deadline.draftAction.label}
                    </DataTableAction>
                  )}
                  {canOverride && deadline.manualOverride && (
                    <DataTableAction
                      disabled={Boolean(overrideSaving)}
                      label="Restaurar estado automático"
                      icon={
                        saving && overrideSaving.status === "automatic" ? (
                          <LoaderCircle className="spin" size={13} />
                        ) : (
                          <RotateCcw size={13} />
                        )
                      }
                      onClick={() =>
                        onSetOverride(deadline.id, "automatic")
                      }
                    >
                      Restaurar
                    </DataTableAction>
                  )}
                  {canOverride && (
                    <DataTableAction
                      disabled={Boolean(overrideSaving)}
                      label={
                        resolved
                          ? "Marcar recordatorio como pendiente"
                          : "Marcar recordatorio como cumplido"
                      }
                      icon={
                        saving &&
                        overrideSaving.status ===
                          (resolved ? "pending" : "completed") ? (
                          <LoaderCircle className="spin" size={13} />
                        ) : resolved ? (
                          <CircleDot size={13} />
                        ) : (
                          <Check size={13} />
                        )
                      }
                      onClick={() =>
                        onSetOverride(
                          deadline.id,
                          resolved ? "pending" : "completed",
                        )
                      }
                    >
                      {resolved ? "Marcar pendiente" : "Marcar cumplido"}
                    </DataTableAction>
                  )}
                </div>
              </DataTableCell>
            </DataTableRow>
          );
        })}
      </DataTableBody>
      </DataTable>
      <DeadlineDraftDialog
        draft={activeDraft}
        onClose={closeDraft}
        onOpenGenerator={onOpenGenerator}
      />
    </>
  );
}

function AcknowledgedInconsistencies({
  items,
  saving,
  onRestore,
}) {
  if (!items.length) return null;
  return (
    <section className="procedure-acknowledged-issues">
      <header>
        <div>
          <CheckCircle2 size={16} aria-hidden="true" />
          <strong>Revisiones procesales atendidas</strong>
        </div>
        <span>{items.length}</span>
      </header>
      <div>
        {items.map((issue) => (
          <article key={issue.id}>
            <div>
              <strong>{issue.title}</strong>
              <span>{issue.description}</span>
              {issue.acknowledgment?.acknowledgedAt && (
                <small>
                  Marcado como revisado el{" "}
                  <RenderedDate
                    value={issue.acknowledgment.acknowledgedAt}
                    withTime
                  />
                </small>
              )}
            </div>
            <DataTableAction
              disabled={Boolean(saving)}
              label="Restaurar revisión procesal"
              icon={
                saving === issue.id ? (
                  <LoaderCircle className="spin" size={13} />
                ) : (
                  <RotateCcw size={13} />
                )
              }
              onClick={() => onRestore(issue.id, false)}
            >
              Restaurar
            </DataTableAction>
          </article>
        ))}
      </div>
    </section>
  );
}

function AutomaticDeadlinesPanel({
  deadlines,
  loading,
  updating,
  selectedDeadlineId,
  onNavigateEvidence,
  representedPartyGroups,
  canOverride,
  overrideSaving,
  overrideError,
  onSetOverride,
  manualReminders,
  manualReminderDraft,
  onManualReminderDraftChange,
  onAddManualReminder,
  onSetManualReminderStatus,
  onDeleteManualReminder,
  manualReminderSaving,
  manualReminderStatusSaving,
  manualReminderDeleting,
  onOpenGenerator,
  acknowledgedInconsistencies = [],
  inconsistencyAcknowledgmentSaving,
  onSetInconsistencyAcknowledgment,
}) {
  const ordered = useMemo(
    () =>
      [...(deadlines || [])].sort((left, right) =>
        compareDeadlineReminders(left, right, {
          representedPartyGroups,
        }),
      ),
    [deadlines, representedPartyGroups],
  );
  const preliminaryWarning = ordered
    .find((deadline) => deadline.calculation?.warnings?.length > 0)
    ?.calculation.warnings[0]
    ?.replace(/^Cómputo preliminar:\s*/i, "");
  const activeCount = ordered.filter(
    ({ lifecycleStatus }) => lifecycleStatus === "open",
  ).length;
  const inactiveCount = ordered.length - activeCount;
  const orderedAttentionItems = useMemo(
    () =>
      orderExpedienteAttentionItems(
        deadlines,
        representedPartyGroups,
        { manualReminders },
      ),
    [deadlines, manualReminders, representedPartyGroups],
  );

  useEffect(() => {
    if (!selectedDeadlineId) return;
    window.requestAnimationFrame(() => {
      document
        .getElementById(`procedure-deadline-${selectedDeadlineId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [selectedDeadlineId]);

  return (
    <DataTableSection
      className="expediente-detail-table-section procedure-deadlines-section"
      description="Recordatorios automáticos y manuales"
      icon={CalendarClock}
      title="Recordatorios"
      actions={
        updating || ordered.length > 0 || manualReminders.length > 0 ? (
          <div className="procedure-deadlines-actions">
            {updating && (
              <span
                className="reminders-refresh-indicator"
                role="status"
                aria-live="polite"
              >
                <LoaderCircle className="spin" size={13} />
                Actualizando
              </span>
            )}
            {(ordered.length > 0 || manualReminders.length > 0) && (
              <span className="procedure-deadlines-count">
                {activeCount} {activeCount === 1 ? "activo" : "activos"}
                {inactiveCount > 0
                  ? ` · ${inactiveCount} sin acción actual`
                  : ""}
                {manualReminders.length > 0
                  ? ` · ${manualReminders.length} manuales`
                  : ""}
              </span>
            )}
          </div>
        ) : null
      }
    >

      {ordered.length > 0 && preliminaryWarning && (
        <div className="procedure-deadline-warning procedure-deadlines-warning">
          <strong>Cómputo preliminar</strong>
          <span>{preliminaryWarning}</span>
        </div>
      )}

      {overrideError && (
        <div className="inline-error procedure-deadline-override-error">
          {overrideError}
        </div>
      )}

      <form
        className="procedure-manual-reminder-form is-top"
        onSubmit={onAddManualReminder}
      >
        <ArgentineDateInput
          value={manualReminderDraft.dueDate}
          onChange={(dueDate) =>
            onManualReminderDraftChange({ dueDate })
          }
        />
        <label>
          <FileText size={15} />
          <input
            value={manualReminderDraft.description}
            maxLength={500}
            placeholder="Agregar un recordatorio manual"
            onChange={(event) =>
              onManualReminderDraftChange({
                description: event.target.value,
              })
            }
          />
        </label>
        <button
          className="button secondary"
          type="submit"
          disabled={
            manualReminderSaving ||
            !manualReminderDraft.dueDate ||
            !manualReminderDraft.description.trim()
          }
        >
          {manualReminderSaving ? (
            <LoaderCircle className="spin" size={14} />
          ) : (
            <Plus size={14} />
          )}
          Agregar
        </button>
      </form>

      {loading && (
        <DetailsSectionLoading
          label="Cargando recordatorios"
          detail="Analizando el estado actual del expediente…"
        />
      )}

      {!loading && ordered.length === 0 && manualReminders.length === 0 ? (
        <div className="procedure-deadlines-empty">
          <CalendarClock size={20} />
          <div>
            <strong>No hay recordatorios</strong>
            <span>
              Los mostraremos cuando detectemos uno automáticamente o agregues
              uno manual.
            </span>
          </div>
        </div>
      ) : orderedAttentionItems.length > 0 ? (
        <DeadlineAttentionTable
          items={orderedAttentionItems}
          selectedDeadlineId={selectedDeadlineId}
          onNavigateEvidence={onNavigateEvidence}
          representedPartyGroups={representedPartyGroups}
          canOverride={canOverride}
          overrideSaving={overrideSaving}
          onSetOverride={onSetOverride}
          manualReminderStatusSaving={manualReminderStatusSaving}
          manualReminderDeleting={manualReminderDeleting}
          onSetManualReminderStatus={onSetManualReminderStatus}
          onDeleteManualReminder={onDeleteManualReminder}
          onOpenGenerator={onOpenGenerator}
        />
      ) : null}

      <AcknowledgedInconsistencies
        items={acknowledgedInconsistencies}
        saving={inconsistencyAcknowledgmentSaving}
        onRestore={onSetInconsistencyAcknowledgment}
      />
    </DataTableSection>
  );
}

function DashboardAttentionTable({
  cuij,
  detailBasePath,
  items,
  inconsistencies,
  onNavigateEvidence,
  onOpenDeadline,
  acknowledgmentSaving,
  onSetInconsistencyAcknowledgment,
}) {
  return (
    <DataTable
      ariaLabel="Atención del expediente"
      className="expediente-dashboard-summary-table is-attention"
      density="compact"
      minWidth="0"
    >
      <DataTableBody>
        {inconsistencies.map((issue) => (
          <DataTableRow
            className="expediente-dashboard-summary-issue-row"
            key={issue.id}
          >
            <DataTableCell colSpan={2}>
              <div className="expediente-dashboard-summary-issue">
                <CircleAlert size={14} aria-hidden="true" />
                <div>
                  <span>Revisión procesal</span>
                  <strong>{issue.title}</strong>
                  <small>{issue.description}</small>
                </div>
                <div className="expediente-dashboard-summary-actions">
                  {issue.data?.orderSourceId && (
                    <DataTableAction
                      label="Ver decreto"
                      icon={<Eye size={12} />}
                      onClick={() =>
                        onNavigateEvidence(issue.data.orderSourceId)
                      }
                    >
                      Decreto
                    </DataTableAction>
                  )}
                  {issue.data?.certificateSourceId && (
                    <DataTableAction
                      label="Ver constancia"
                      icon={<Eye size={12} />}
                      onClick={() =>
                        onNavigateEvidence(issue.data.certificateSourceId)
                      }
                    >
                      Constancia
                    </DataTableAction>
                  )}
                  <WhatsappNoticeCopyButton
                    notice={issue.whatsappNotice}
                    compact
                  />
                  <DataTableAction
                    disabled={Boolean(acknowledgmentSaving)}
                    label="Marcar revisión procesal como revisada"
                    icon={
                      acknowledgmentSaving === issue.id ? (
                        <LoaderCircle className="spin" size={12} />
                      ) : (
                        <Check size={12} />
                      )
                    }
                    onClick={() =>
                      onSetInconsistencyAcknowledgment(issue.id, true)
                    }
                  >
                    Marcar revisado
                  </DataTableAction>
                </div>
              </div>
            </DataTableCell>
          </DataTableRow>
        ))}
        {items.map((attentionItem) => {
          if (attentionItem.type === "manual") {
            const reminder = attentionItem.value;
            return (
              <DataTableRow
                className={`is-tone-${attentionItem.tone}`}
                key={attentionItem.id}
              >
                <DataTableCell>
                  <AppLink
                    className="expediente-dashboard-summary-link"
                    to={expedienteDetailTabPath(
                      cuij,
                      "reminders",
                      detailBasePath,
                    )}
                  >
                    <DataTablePrimaryCell
                      title={reminder.description}
                      badges={(
                        <DataTableBadge tone={attentionItem.tone}>
                          Recordatorio manual
                        </DataTableBadge>
                      )}
                    />
                  </AppLink>
                </DataTableCell>
                <DataTableCell
                  className="expediente-dashboard-summary-date"
                  align="end"
                  priority="secondary"
                >
                  <DataTableValue
                    primary={<RenderedDateOnly value={reminder.dueDate} />}
                  />
                </DataTableCell>
              </DataTableRow>
            );
          }
          const deadline = attentionItem.value;
          const deadlineDate = deadlineDatePresentation(deadline);
          return (
            <DataTableRow
              className={`is-tone-${attentionItem.tone}`}
              key={attentionItem.id}
            >
              <DataTableCell>
                <button
                  type="button"
                  className="expediente-dashboard-summary-link"
                  onClick={() => onOpenDeadline(deadline.id)}
                >
                  <DataTablePrimaryCell
                    title={deadline.label}
                    badges={(
                      <DataTableBadge tone={attentionItem.tone}>
                        {deadlineStatusCopy(deadline)}
                      </DataTableBadge>
                    )}
                  />
                </button>
              </DataTableCell>
              <DataTableCell
                className="expediente-dashboard-summary-date"
                align="end"
                priority="secondary"
              >
                <DataTableValue
                  primary={deadlineDate.rawValue ? (
                    <RenderedDateOnly value={deadlineDate.rawValue} />
                  ) : deadlineDate.value}
                  secondary={[
                    deadlineDate.label,
                    deadline.duePartyLabel,
                  ].filter(Boolean).join(" · ")}
                />
              </DataTableCell>
            </DataTableRow>
          );
        })}
      </DataTableBody>
    </DataTable>
  );
}

function DashboardDocumentsTable({
  documents,
  documentLoading,
  onOpenDocument,
}) {
  return (
    <DataTable
      ariaLabel="Documentos importantes"
      className="expediente-dashboard-summary-table is-documents"
      density="compact"
      minWidth="0"
    >
      <DataTableBody>
        {documents.map((document) => {
          const documentId = actuacionDocumentId(document.actuacion);
          const isLoading = Boolean(documentId) && documentLoading === documentId;
          return (
            <DataTableRow
              key={document.key}
              label={`Ver ${document.title}`}
              onActivate={
                documentLoading
                  ? undefined
                  : () => onOpenDocument(document.actuacion)
              }
            >
              <DataTableCell>
                <DataTablePrimaryCell
                  icon={isLoading
                    ? <LoaderCircle className="spin" size={14} />
                    : <FileText size={14} />}
                  title={document.title}
                  meta={document.subtitle}
                />
              </DataTableCell>
              <DataTableCell
                className="expediente-dashboard-summary-file"
                align="end"
              >
                <DataTableAction
                  disabled={Boolean(documentLoading)}
                  label={`Ver PDF de ${document.title}`}
                  icon={<Eye size={13} />}
                  onClick={() => onOpenDocument(document.actuacion)}
                >
                  PDF
                </DataTableAction>
              </DataTableCell>
            </DataTableRow>
          );
        })}
      </DataTableBody>
    </DataTable>
  );
}

function DashboardContactsTable({ contacts }) {
  return (
    <DataTable
      ariaLabel="Partes y abogados del expediente"
      className="expediente-dashboard-summary-table is-contacts"
      density="compact"
      minWidth="0"
    >
      <DataTableBody>
        {contacts.map((entry, index) => {
          const activeRoles = (entry.roleDetails || []).filter(
            ({ historical }) => !historical,
          );
          const role = (activeRoles[0] || entry.roleDetails?.[0])?.label ||
            (entry.group === "lawyer" ? "Abogado/a" : "Parte");
          const representedParty = (
            activeRoles[0] || entry.roleDetails?.[0]
          )?.representedParty;
          const primaryContact = entry.contacts?.[0];
          return (
            <DataTableRow key={entry.id || `${entry.name}:${index}`}>
              <DataTableCell>
                <DataTablePrimaryCell
                  title={entry.name || "Nombre no informado"}
                  meta={[
                    role,
                    representedParty ? `Representa a ${representedParty}` : null,
                  ].filter(Boolean).join(" · ")}
                  badges={(
                    <DataTableBadge>
                      {entry.group === "lawyer" ? "Abogado/a" : "Parte"}
                    </DataTableBadge>
                  )}
                />
              </DataTableCell>
              <DataTableCell
                className="expediente-dashboard-contact-value"
                align="end"
                priority="secondary"
              >
                {primaryContact ? (
                  <DirectoryContact contact={primaryContact} compact />
                ) : (
                  <span>Sin datos de contacto</span>
                )}
              </DataTableCell>
            </DataTableRow>
          );
        })}
      </DataTableBody>
    </DataTable>
  );
}

function DashboardActuacionesTable({ actuaciones, onOpenActuacion }) {
  return (
    <DataTable
      ariaLabel="Últimas diez actuaciones del expediente"
      className="expediente-dashboard-summary-table is-actuaciones"
      density="compact"
      minWidth="0"
    >
      <DataTableBody>
        {actuaciones.map((actuacion) => {
          const sourceId = timelineSourceIds(actuacion)[0];
          const title = actuacion.titulo || actuacion.tipo || "Actuación sin título";
          const meta = [
            actuacion.tipo && actuacion.tipo !== title ? actuacion.tipo : null,
            actuacion.organismoCreador,
          ].filter(Boolean).join(" · ");
          return (
            <DataTableRow
              key={actuacion.entryId}
              label={sourceId ? `Ver ${title}` : null}
              onActivate={sourceId
                ? () => onOpenActuacion(actuacion)
                : undefined}
            >
              <DataTableCell>
                <DataTablePrimaryCell
                  icon={<FileText size={14} />}
                  title={title}
                  meta={meta}
                  badges={(
                    <DataTableBadge>{getActuacionCategory(actuacion)}</DataTableBadge>
                  )}
                />
              </DataTableCell>
              <DataTableCell
                className="expediente-dashboard-actuacion-date"
                align="end"
                priority="secondary"
              >
                <DataTableValue
                  primary={<RenderedTimelineDate value={actuacion.occurredAt} />}
                />
              </DataTableCell>
            </DataTableRow>
          );
        })}
      </DataTableBody>
    </DataTable>
  );
}

function ExpedienteDashboard({
  cuij,
  detailBasePath,
  attentionLoading,
  attentionUpdating,
  importantDeadlines,
  manualReminders,
  representedPartyGroups,
  documents,
  documentCount,
  documentsLoading,
  documentLoading,
  contacts,
  contactsLoading,
  actuaciones,
  actuacionesLoading,
  onOpenDeadline,
  onOpenDocument,
  onOpenActuacion,
  manualReminderDraft,
  onManualReminderDraftChange,
  onAddManualReminder,
  manualReminderSaving,
  inconsistencies = [],
  onNavigateEvidence,
  inconsistencyAcknowledgmentSaving,
  onSetInconsistencyAcknowledgment,
}) {
  const [manualReminderOpen, setManualReminderOpen] = useState(false);
  const orderedAttentionItems = useMemo(
    () =>
      orderExpedienteAttentionItems(
        importantDeadlines,
        representedPartyGroups,
        { manualReminders },
      ).filter(({ tone }) => isImportantReminderTone(tone)),
    [
      importantDeadlines,
      manualReminders,
      representedPartyGroups,
    ],
  );
  const reminderContentState = reminderDashboardContentState({
    loading: attentionLoading,
    updating: attentionUpdating,
    hasItems:
      orderedAttentionItems.length > 0 || inconsistencies.length > 0,
  });
  return (
    <section
      id="expediente-panel-dashboard"
      className="expediente-dashboard-grid"
      role="tabpanel"
      aria-labelledby="expediente-tab-dashboard"
    >
      <article className="expediente-dashboard-card is-reminders">
        <header>
          <span className="expediente-dashboard-card-icon is-danger">
            <CalendarClock size={17} />
          </span>
          <div>
            <span>Rojos y amarillos</span>
            <h2>Recordatorios importantes</h2>
          </div>
          <div className="expediente-dashboard-card-actions">
            {attentionUpdating && (
              <span
                className="reminders-refresh-indicator"
                role="status"
                aria-live="polite"
              >
                <LoaderCircle className="spin" size={12} />
                Actualizando
              </span>
            )}
            <button
              type="button"
              aria-expanded={manualReminderOpen}
              onClick={() => setManualReminderOpen((current) => !current)}
            >
              {manualReminderOpen ? <X size={13} /> : <Plus size={13} />}
              {manualReminderOpen ? "Cancelar" : "Agregar"}
            </button>
            <AppLink
              to={expedienteDetailTabPath(
                cuij,
                "reminders",
                detailBasePath,
              )}
            >
              Ver recordatorios <ChevronRight size={14} />
            </AppLink>
          </div>
        </header>
        {manualReminderOpen && (
          <form
            className="expediente-dashboard-reminder-form"
            onSubmit={onAddManualReminder}
          >
            <ArgentineDateInput
              value={manualReminderDraft.dueDate}
              onChange={(dueDate) =>
                onManualReminderDraftChange({ dueDate })
              }
            />
            <label>
              <FileText size={14} />
              <input
                autoFocus
                value={manualReminderDraft.description}
                maxLength={500}
                placeholder="Descripción del recordatorio"
                onChange={(event) =>
                  onManualReminderDraftChange({
                    description: event.target.value,
                  })
                }
              />
            </label>
            <button
              className="button secondary"
              type="submit"
              disabled={
                manualReminderSaving ||
                !manualReminderDraft.dueDate ||
                !manualReminderDraft.description.trim()
              }
            >
              {manualReminderSaving ? (
                <LoaderCircle className="spin" size={14} />
              ) : (
                <Plus size={14} />
              )}
              Guardar
            </button>
          </form>
        )}
        {reminderContentState === "loading" ? (
          <div className="expediente-dashboard-state">
            <LoaderCircle className="spin" size={17} />
            <span>Preparando los recordatorios…</span>
          </div>
        ) : reminderContentState === "items" ? (
          <div className="expediente-dashboard-reminders">
            <DashboardAttentionTable
              cuij={cuij}
              detailBasePath={detailBasePath}
              items={orderedAttentionItems}
              inconsistencies={inconsistencies}
              onNavigateEvidence={onNavigateEvidence}
              onOpenDeadline={onOpenDeadline}
              acknowledgmentSaving={inconsistencyAcknowledgmentSaving}
              onSetInconsistencyAcknowledgment={
                onSetInconsistencyAcknowledgment
              }
            />
          </div>
        ) : reminderContentState === "updating" ? (
          <div className="expediente-dashboard-state">
            <LoaderCircle className="spin" size={17} />
            <span>Revisando recordatorios importantes…</span>
          </div>
        ) : (
          <div className="expediente-dashboard-state is-success">
            <CheckCircle2 size={17} />
            <span>No hay recordatorios importantes.</span>
          </div>
        )}
      </article>

      <article className="expediente-dashboard-card is-documents">
        <header>
          <span className="expediente-dashboard-card-icon">
            <Paperclip size={17} />
          </span>
          <div>
            <span>
              {documentsLoading
                ? "Buscando"
                : `${documentCount} ${documentCount === 1 ? "encontrado" : "encontrados"}`}
            </span>
            <h2>Documentos importantes</h2>
          </div>
          <AppLink
            to={expedienteDetailTabPath(
              cuij,
              "documents",
              detailBasePath,
            )}
          >
            Ver documentos <ChevronRight size={14} />
          </AppLink>
        </header>
        {documentsLoading ? (
          <div className="expediente-dashboard-state">
            <LoaderCircle className="spin" size={17} />
            <span>Buscando documentos importantes…</span>
          </div>
        ) : documents.length ? (
          <div className="expediente-dashboard-documents">
            <DashboardDocumentsTable
              documents={documents}
              documentLoading={documentLoading}
              onOpenDocument={onOpenDocument}
            />
          </div>
        ) : (
          <div className="expediente-dashboard-state is-muted">
            <FileText size={17} />
            <span>No detectamos sentencias ni informes periciales.</span>
          </div>
        )}
      </article>

      <article className="expediente-dashboard-card is-contacts">
        <header>
          <span className="expediente-dashboard-card-icon">
            <UsersRound size={17} />
          </span>
          <div>
            <span>
              {contactsLoading
                ? "Buscando"
                : `${contacts.length} ${contacts.length === 1 ? "contacto" : "contactos"}`}
            </span>
            <h2>Contactos</h2>
          </div>
          <AppLink
            to={expedienteDetailTabPath(
              cuij,
              "information",
              detailBasePath,
            )}
          >
            Ver más <ChevronRight size={14} />
          </AppLink>
        </header>
        {contactsLoading ? (
          <div className="expediente-dashboard-state">
            <LoaderCircle className="spin" size={17} />
            <span>Cargando partes y abogados…</span>
          </div>
        ) : contacts.length ? (
          <div className="expediente-dashboard-contacts">
            <DashboardContactsTable contacts={contacts} />
          </div>
        ) : (
          <div className="expediente-dashboard-state is-muted">
            <UsersRound size={17} />
            <span>No hay partes ni abogados informados.</span>
          </div>
        )}
      </article>

      <article className="expediente-dashboard-card is-actuaciones">
        <header>
          <span className="expediente-dashboard-card-icon">
            <FileClock size={17} />
          </span>
          <div>
            <span>Actuaciones, escritos y cédulas</span>
            <h2>Últimas 10 actuaciones</h2>
          </div>
          <AppLink
            to={expedienteDetailTabPath(
              cuij,
              "actuaciones",
              detailBasePath,
            )}
          >
            Ver actuaciones <ChevronRight size={14} />
          </AppLink>
        </header>
        {actuacionesLoading ? (
          <div className="expediente-dashboard-state">
            <LoaderCircle className="spin" size={17} />
            <span>Cargando actuaciones…</span>
          </div>
        ) : actuaciones.length ? (
          <div className="expediente-dashboard-actuaciones">
            <DashboardActuacionesTable
              actuaciones={actuaciones}
              onOpenActuacion={onOpenActuacion}
            />
          </div>
        ) : (
          <div className="expediente-dashboard-state is-muted">
            <FileText size={17} />
            <span>No hay actuaciones, escritos ni cédulas.</span>
          </div>
        )}
      </article>
    </section>
  );
}

function AiReadProgress({ state, checking, retrying, onRetry }) {
  const [expanded, setExpanded] = useState(false);
  const statusRef = useRef(null);
  const progress = state?.progress || {};
  const awaitingCredits =
    state?.access?.billing?.status === "awaiting_credits";
  const loadingState = !state;
  const processing = ["pending", "processing"].includes(progress.status);
  const failed = progress.status === "failed";
  const analysisNotStarted = Boolean(
    state &&
    !state.enabled &&
    !state.intelligence,
  );
  const waitingForAnalysis = Boolean(
    state &&
    state.enabled &&
    !state.intelligence &&
    !processing &&
    !failed,
  );
  const title = loadingState
    ? "Cargando el análisis del expediente…"
    : analysisNotStarted
      ? "Este expediente todavía no fue analizado por IA"
      : awaitingCredits
        ? "Necesitás créditos para habilitar la inteligencia"
      : waitingForAnalysis
      ? "Preparando el análisis del expediente…"
      : checking && !processing
      ? "Buscando nuevas actuaciones…"
      : failed
        ? "La lectura IA necesita atención"
        : processing
          ? progress.phase === "aggregating"
            ? "Actualizando el análisis del expediente"
            : "La IA está leyendo el expediente"
          : "IA actualizada";
  const detail = loadingState
    ? "Consultando el análisis disponible."
    : analysisNotStarted
      ? "La lectura IA aún no fue iniciada."
      : awaitingCredits
        ? `Este acceso requiere ${formatCredits(state.access.billing.credits)} créditos. Podés agregar créditos y reintentar sin volver a analizar el expediente.`
      : waitingForAnalysis
      ? "La lectura IA todavía no está disponible."
      : processing
      ? progress.phase === "queued"
        ? "El análisis está en cola y comenzará en breve."
        : progress.phase === "aggregating"
          ? "Generando el resumen, el estado, los contactos y los recordatorios."
          : `${progress.processedItems || 0} de ${progress.totalItems || 0} actuaciones procesadas`
      : "El análisis incorpora la información disponible hasta la última actualización.";

  useEffect(() => {
    if (!expanded) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!statusRef.current?.contains(event.target)) setExpanded(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setExpanded(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [expanded]);

  return (
    <div
      className={`expediente-ai-status is-${progress.status || "loading"} ${
        expanded ? "is-open" : ""
      }`}
      ref={statusRef}
      aria-live="polite"
    >
      <button
        className="expediente-ai-status-trigger"
        type="button"
        aria-controls="expediente-ai-status-details"
        aria-expanded={expanded}
        title={`${title}. ${expanded ? "Ocultar" : "Ver"} detalles`}
        onClick={() => setExpanded((current) => !current)}
      >
        <span className="expediente-ai-status-icon" aria-hidden="true">
          {loadingState || waitingForAnalysis || processing || checking ? (
            <Sparkles className="ai-pulse" size={15} />
          ) : failed ? (
            <RefreshCw size={14} />
          ) : (
            <CheckCircle2 size={14} />
          )}
        </span>
        <span className="expediente-ai-status-label">{title}</span>
        <ChevronDown
          className="expediente-ai-status-chevron"
          size={14}
          aria-hidden="true"
        />
      </button>

      {expanded && (
        <div
          className="expediente-ai-status-details"
          id="expediente-ai-status-details"
          role="region"
          aria-label={`Detalles: ${title}`}
        >
          <strong>{title}</strong>
          <p>{detail}</p>
          {(analysisNotStarted || awaitingCredits || failed) && (
            <button
              className={analysisNotStarted || awaitingCredits
                ? "button primary"
                : "button secondary"}
              type="button"
              onClick={onRetry}
              disabled={retrying}
            >
              {retrying && <LoaderCircle className="spin" size={16} />}
              {analysisNotStarted
                ? retrying
                  ? "Iniciando…"
                  : "Analizar con IA"
                : awaitingCredits
                  ? retrying
                    ? "Reintentando…"
                    : "Reintentar cobro"
                : retrying
                  ? "Reintentando…"
                  : "Reintentar"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function ExpedienteDetailPage({
  api,
  backLabel = null,
  backPath = null,
  basePath = null,
  identifier,
  productName = "Praxis",
  renderDrafts: WorkspaceDrafts = null,
  readOnly = false,
  sourceId,
  toDocument = (value) => value,
}) {
  const { path, search: routeSearch, navigate } = useRouter();
  const sourceAdapter = expedienteSourceAdapterFor(sourceId);
  const detailBasePath = basePath || (
    path.startsWith("/seguimientos/") ? "/seguimientos" : "/expedientes"
  );
  const isFollowUpContext = !readOnly && detailBasePath === "/seguimientos";
  const resolvedBackPath = backPath || (
    isFollowUpContext ? "/seguimientos" : "/expedientes"
  );
  const resolvedBackLabel = backLabel || (
    isFollowUpContext ? "Mis seguimientos" : "Mis expedientes"
  );
  const expedienteRouteParts = path
    .slice(`${detailBasePath}/`.length)
    .split("/");
  const cuij = identifier || decodeURIComponent(expedienteRouteParts[0]);
  const requestedDetailSlug = expedienteRouteParts[1] || "";
  const requestedDetailTab =
    EXPEDIENTE_DETAIL_TABS.find(
      ({ slug, legacySlugs = [] }) =>
        slug === requestedDetailSlug ||
        legacySlugs.includes(requestedDetailSlug),
    )?.id || (readOnly ? "information" : "dashboard");
  const routedWorkspaceDocumentId = requestedDetailTab === "drafts"
    ? decodeURIComponent(expedienteRouteParts[2] || "")
    : "";
  const routeActuacionId = new URLSearchParams(routeSearch).get(
    "actuacion",
  );
  const routedReminderId = new URLSearchParams(routeSearch).get(
    "recordatorio",
  ) || "";
  const routedDocument = readDocumentViewerRoute(routeSearch, cuij);
  const [expediente, setExpediente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [retryingDetails, setRetryingDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addingFollowUp, setAddingFollowUp] = useState(false);
  const [archiveAction, setArchiveAction] = useState(null);
  const [changingArchive, setChangingArchive] = useState(false);
  const [removeSavedOpen, setRemoveSavedOpen] = useState(false);
  const [removingSaved, setRemovingSaved] = useState(false);
  const [removeFollowUpOpen, setRemoveFollowUpOpen] = useState(false);
  const [removingFollowUp, setRemovingFollowUp] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [timelineExpanded, setTimelineExpanded] = useState(
    Boolean(routeActuacionId) || requestedDetailTab === "actuaciones",
  );
  const [timelineLoaded, setTimelineLoaded] = useState(false);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState("");
  const [activeTimelineFilters, setActiveTimelineFilters] = useState([
    "actuaciones",
    "escritos",
    "cedulas",
  ]);
  const [documentLoading, setDocumentLoading] = useState("");
  const [attachmentLoading, setAttachmentLoading] = useState("");
  const [documentDialog, setDocumentDialog] = useState(null);
  const [pdfViewer, setPdfViewer] = useState(null);
  const [relatedSentenceSources, setRelatedSentenceSources] = useState([]);
  const [relatedSentencesLoading, setRelatedSentencesLoading] = useState(false);
  const [relatedSentencesError, setRelatedSentencesError] = useState("");
  const [procedureState, setProcedureState] = useState(null);
  const [procedureLoading, setProcedureLoading] = useState(false);
  const [procedureError, setProcedureError] = useState("");
  const [aiReadState, setAiReadState] = useState(null);
  const [aiReadChecking, setAiReadChecking] = useState(false);
  const [retryingAiRead, setRetryingAiRead] = useState(false);
  const [
    inconsistencyAcknowledgmentSaving,
    setInconsistencyAcknowledgmentSaving,
  ] = useState("");
  const [selectedDeadlineId, setSelectedDeadlineId] = useState(
    routedReminderId,
  );
  const [deadlineOverrideSaving, setDeadlineOverrideSaving] = useState(null);
  const [deadlineOverrideError, setDeadlineOverrideError] = useState("");
  const [manualReminderDraft, setManualReminderDraft] = useState(() => ({
    dueDate: todayInMendoza(),
    description: "",
  }));
  const [manualReminderSaving, setManualReminderSaving] = useState(false);
  const [manualReminderStatusSaving, setManualReminderStatusSaving] =
    useState("");
  const [manualReminderDeleting, setManualReminderDeleting] = useState("");
  const [jumpCuij, setJumpCuij] = useState("");
  const [jumpError, setJumpError] = useState("");
  const [workspaceLawyers, setWorkspaceLawyers] = useState([]);
  const [highlightedTimelineEntry, setHighlightedTimelineEntry] =
    useState(null);
  const timelineHighlightTimeoutRef = useRef(null);
  const routeActuacionHandledRef = useRef("");
  const refreshInFlightRef = useRef(false);
  const automaticRefreshCuijRef = useRef("");
  const timelineRequestRef = useRef(null);
  const pendingTimelineSourceRef = useRef("");
  const previousDetailsLoadingRef = useRef(false);
  const isManagedExpediente = Boolean(
    !readOnly && sourceAdapter.manageable && (
      expediente?.isSaved || expediente?.isArchived || expediente?.isFollowed
    ),
  );
  const activeDetailTab =
    expediente
    && !isManagedExpediente
    && !isUnmanagedExpedienteDetailTab(requestedDetailTab)
      ? "information"
      : requestedDetailTab;
  const visibleDetailTabs = isManagedExpediente
    ? EXPEDIENTE_DETAIL_TABS
    : EXPEDIENTE_DETAIL_TABS.filter(({ id }) =>
      isUnmanagedExpedienteDetailTab(id));

  useEffect(() => {
    setJumpCuij("");
    setJumpError("");
    setHighlightedTimelineEntry(null);
    routeActuacionHandledRef.current = "";
    automaticRefreshCuijRef.current = "";
    refreshInFlightRef.current = false;
    timelineRequestRef.current = null;
    pendingTimelineSourceRef.current = "";
    previousDetailsLoadingRef.current = false;
    setSelectedDeadlineId(routedReminderId);
    setDeadlineOverrideSaving(null);
    setDeadlineOverrideError("");
    setManualReminderDraft({
      dueDate: todayInMendoza(),
      description: "",
    });
    setManualReminderSaving(false);
    setManualReminderStatusSaving("");
    setManualReminderDeleting("");
    setTimelineExpanded(Boolean(routeActuacionId));
    setTimelineLoaded(false);
    setTimelineLoading(false);
    setTimelineError("");
    setRelatedSentenceSources([]);
    setRelatedSentencesLoading(false);
    setRelatedSentencesError("");
    setAiReadState(null);
    setAiReadChecking(false);
    setInconsistencyAcknowledgmentSaving("");
    window.clearTimeout(timelineHighlightTimeoutRef.current);
  }, [cuij, routeActuacionId, routedReminderId]);

  const setDeadlineOverride = useCallback(
    async (deadlineId, status) => {
      try {
        setDeadlineOverrideError("");
        setDeadlineOverrideSaving({ deadlineId, status });
        const next = await api.setExpedienteDeadlineOverride(
          cuij,
          deadlineId,
          status,
        );
        setProcedureState(next);
      } catch (requestError) {
        setDeadlineOverrideError(requestError.message);
      } finally {
        setDeadlineOverrideSaving(null);
      }
    },
    [api, cuij],
  );

  const setInconsistencyAcknowledgment = useCallback(
    async (inconsistencyId, acknowledged) => {
      try {
        setError("");
        setInconsistencyAcknowledgmentSaving(inconsistencyId);
        const next = await api.setExpedienteInconsistencyAcknowledgment(
          cuij,
          inconsistencyId,
          acknowledged,
        );
        setAiReadState(next);
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setInconsistencyAcknowledgmentSaving("");
      }
    },
    [api, cuij],
  );

  const addManualReminder = useCallback(
    async (event, { redirectToReminders = false } = {}) => {
      event.preventDefault();
      if (!manualReminderDraft.dueDate || !manualReminderDraft.description.trim()) {
        return;
      }
      try {
        setError("");
        setManualReminderSaving(true);
        const followUp = await api.addExpedienteManualReminder(cuij, {
          dueDate: manualReminderDraft.dueDate,
          description: manualReminderDraft.description.trim(),
        });
        setExpediente((current) => ({
          ...current,
          isFollowed: true,
          followUpId: followUp.id,
          manualReminders: followUp.reminders || [],
        }));
        setManualReminderDraft({
          dueDate: todayInMendoza(),
          description: "",
        });
        if (redirectToReminders) {
          navigate(
            expedienteDetailTabPath(
              cuij,
              "reminders",
              detailBasePath,
            ),
          );
        }
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setManualReminderSaving(false);
      }
    },
    [api, cuij, detailBasePath, manualReminderDraft, navigate],
  );

  const setManualReminderStatus = useCallback(
    async (reminderId, status) => {
      if (!expediente?.followUpId) return;
      try {
        setError("");
        setManualReminderStatusSaving(reminderId);
        const followUp = await api.setFollowUpReminderStatus(
          expediente.followUpId,
          reminderId,
          status,
        );
        setExpediente((current) => ({
          ...current,
          manualReminders: followUp.reminders || [],
        }));
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setManualReminderStatusSaving("");
      }
    },
    [api, expediente?.followUpId],
  );

  const deleteManualReminder = useCallback(
    async (reminder) => {
      if (!expediente?.followUpId) return;
      if (
        !window.confirm(
          `¿Eliminar el recordatorio “${reminder.description}”? Esta acción no se puede deshacer.`,
        )
      ) {
        return;
      }
      try {
        setError("");
        setManualReminderDeleting(reminder.id);
        const followUp = await api.dismissFollowUpReminder(
          expediente.followUpId,
          reminder.id,
        );
        setExpediente((current) => ({
          ...current,
          manualReminders: followUp.reminders || [],
        }));
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setManualReminderDeleting("");
      }
    },
    [api, expediente?.followUpId],
  );

  useEffect(
    () => () =>
      window.clearTimeout(timelineHighlightTimeoutRef.current),
    [],
  );

  useEffect(
    () => () => {
      if (pdfViewer?.url) URL.revokeObjectURL(pdfViewer.url);
    },
    [pdfViewer?.url],
  );

  useEffect(() => {
    if (!routedDocument.actId) {
      setPdfViewer(null);
      setDocumentDialog((current) => current?.routed ? null : current);
      setDocumentLoading("");
      setAttachmentLoading("");
      return undefined;
    }
    if (!sourceAdapter.manageable) {
      setPdfViewer(null);
      setDocumentLoading("");
      setAttachmentLoading("");
      setDocumentDialog({
        routed: true,
        kind: "message",
        title: "PDF todavía no disponible",
        message:
          `La fuente informó el documento, pero todavía no está habilitada su apertura desde ${productName}.`,
      });
      return undefined;
    }

    let active = true;
    setDocumentDialog(null);
    setPdfViewer(null);
    if (routedDocument.attachmentId) {
      setAttachmentLoading(routedDocument.attachmentId);
      setDocumentLoading("");
    } else {
      setDocumentLoading(routedDocument.actId);
      setAttachmentLoading("");
    }

    api.actuacionDocument({
      cuij: routedDocument.cuij,
      actId: routedDocument.actId,
      attachmentId: routedDocument.attachmentId || undefined,
    }).then((result) => {
      if (!active) return;
      if (result?.status === "pdf" && result.blob?.size) {
        setPdfViewer({
          url: URL.createObjectURL(result.blob),
          fileName: result.fileName || "actuacion.pdf",
        });
        return;
      }
      if (result?.status === "select_attachment") {
        setDocumentDialog({
          routed: true,
          kind: "attachments",
          actId: routedDocument.actId,
          attachments: result.attachments || [],
        });
        return;
      }
      setDocumentDialog({
        routed: true,
        kind: "message",
        title: "PDF no disponible",
        message:
          "El Poder Judicial no informó un PDF ni archivos adjuntos para esta actuación.",
      });
    }).catch((requestError) => {
      if (!active) return;
      setDocumentDialog({
        routed: true,
        kind: "message",
        title: routedDocument.attachmentId
          ? "No pudimos abrir el adjunto"
          : "No pudimos abrir el PDF",
        message: requestError.message,
      });
    }).finally(() => {
      if (!active) return;
      setDocumentLoading("");
      setAttachmentLoading("");
    });

    return () => {
      active = false;
    };
  }, [
    api,
    routedDocument.actId,
    routedDocument.attachmentId,
    routedDocument.cuij,
    productName,
    sourceAdapter,
  ]);

  const closeRoutedDocument = () =>
    closeDocumentViewerRoute({ path, search: routeSearch, navigate });

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      setExpediente(null);
      setExpediente(await sourceAdapter.getCase(api, cuij));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [cuij, sourceAdapter]);

  useEffect(() => {
    if (requestedDetailSlug === "plazos") {
      navigate(
        expedienteDetailTabPath(cuij, "reminders", detailBasePath),
        { replace: true },
      );
      return;
    }
    if (!["checklist", "notificaciones"].includes(requestedDetailSlug)) return;
    navigate(
      expedienteDetailTabPath(cuij, "dashboard", detailBasePath),
      { replace: true },
    );
  }, [cuij, detailBasePath, navigate, requestedDetailSlug]);

  useEffect(() => {
    if (!expediente) return;
    let targetBasePath = null;
    if (isFollowUpContext && !expediente.isFollowed) {
      targetBasePath = "/expedientes";
    } else if (
      !isFollowUpContext &&
      expediente.isFollowed &&
      !expediente.isSaved
    ) {
      targetBasePath = "/seguimientos";
    }
    if (!targetBasePath) return;
    navigate(
      expedienteDetailTabPath(
        cuij,
        requestedDetailTab,
        targetBasePath,
      ),
      { replace: true },
    );
  }, [
    cuij,
    expediente,
    isFollowUpContext,
    navigate,
    requestedDetailTab,
  ]);

  useEffect(() => {
    if (
      !expediente ||
      isManagedExpediente ||
      (isFollowUpContext && !expediente.isFollowed) ||
      isUnmanagedExpedienteDetailTab(requestedDetailTab)
    ) {
      return;
    }
    navigate(
      expedienteDetailTabPath(
        cuij,
        "information",
        detailBasePath,
      ),
      { replace: true },
    );
  }, [
    cuij,
    detailBasePath,
    expediente,
    isFollowUpContext,
    isManagedExpediente,
    navigate,
    requestedDetailTab,
  ]);

  useEffect(() => {
    if (readOnly) {
      setWorkspaceLawyers([]);
      return undefined;
    }
    let active = true;
    api
      .listPraxisLawyers()
      .then((lawyers) => {
        if (active) setWorkspaceLawyers(lawyers);
      })
      .catch(() => {
        if (active) setWorkspaceLawyers([]);
      });
    return () => {
      active = false;
    };
  }, [api, readOnly]);

  const detailsStatus = expediente?.dataSync?.details?.status || "ready";
  const detailsLoading =
    detailsStatus === "pending" || detailsStatus === "processing";
  const detailsFailed = detailsStatus === "failed";
  const hasDetails =
    Boolean(expediente?.partes?.length) ||
    Boolean(expediente?.relacionados?.length) ||
    Boolean(expediente?.objetoJuicios?.length);

  const loadTimeline = useCallback(async ({ force = false } = {}) => {
    if (!force && timelineLoaded) return null;
    if (timelineRequestRef.current?.cuij === cuij) {
      return timelineRequestRef.current.promise;
    }

    setTimelineLoading(true);
    setTimelineError("");
    const promise = sourceAdapter
      .getTimeline(api, cuij, expediente)
      .then((timeline) => {
        setExpediente((current) => ({
          ...current,
          actuaciones: timeline.actuaciones || [],
          movimientos: timeline.movimientos || [],
          actuacionesCount: timeline.actuacionesCount,
          movimientosCount: timeline.movimientosCount,
          detailsRefreshedAt:
            timeline.detailsRefreshedAt || current?.detailsRefreshedAt,
          praxisRefreshedAt:
            timeline.praxisRefreshedAt || current?.praxisRefreshedAt,
        }));
        setTimelineLoaded(true);
        return timeline;
      })
      .catch((requestError) => {
        setTimelineError(requestError.message);
        return null;
      })
      .finally(() => {
        if (timelineRequestRef.current?.promise === promise) {
          timelineRequestRef.current = null;
        }
        setTimelineLoading(false);
      });
    timelineRequestRef.current = { cuij, promise };
    return promise;
  }, [api, cuij, expediente, sourceAdapter, timelineLoaded]);

  useEffect(() => {
    if (!["dashboard", "actuaciones", "documents"].includes(activeDetailTab)) return;
    if (activeDetailTab === "actuaciones") setTimelineExpanded(true);
    void loadTimeline();
  }, [activeDetailTab, loadTimeline]);

  useEffect(() => {
    if (!routeActuacionId) return;
    setTimelineExpanded(true);
    void loadTimeline();
  }, [loadTimeline, routeActuacionId]);

  useEffect(() => {
    const wasLoading = previousDetailsLoadingRef.current;
    previousDetailsLoadingRef.current = detailsLoading;
    if (
      wasLoading &&
      !detailsLoading &&
      timelineExpanded &&
      timelineLoaded
    ) {
      void loadTimeline({ force: true });
    }
  }, [
    detailsLoading,
    loadTimeline,
    timelineExpanded,
    timelineLoaded,
  ]);

  useEffect(() => {
    if (!expediente || readOnly) {
      setProcedureState(null);
      setProcedureLoading(false);
      setProcedureError("");
      return undefined;
    }
    if (
      !expediente.isSaved &&
      !expediente.isArchived &&
      !expediente.isFollowed
    ) {
      setProcedureState(null);
      setProcedureLoading(false);
      setProcedureError("");
      return undefined;
    }
    if (detailsLoading) {
      setProcedureState((current) => current?.evaluation
        ? { ...current, status: "updating" }
        : {
            eligible: true,
            status: "waiting_for_details",
          });
      setProcedureLoading(false);
      return undefined;
    }

    let cancelled = false;
    setProcedureLoading(!procedureState?.evaluation);
    setProcedureError("");
    api
      .getExpedienteProcedureState(cuij)
      .then((next) => {
        if (!cancelled) setProcedureState(next);
      })
      .catch((requestError) => {
        if (!cancelled) setProcedureError(requestError.message);
      })
      .finally(() => {
        if (!cancelled) setProcedureLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    api,
    cuij,
    expediente?._id,
    expediente?.isSaved,
    expediente?.isArchived,
    expediente?.isFollowed,
    expediente?.dataSync?.generation,
    readOnly,
    detailsLoading,
    detailsStatus,
    aiReadState?.progress?.status,
    aiReadState?.intelligence?.generatedAt,
  ]);

  useEffect(() => {
    if (!detailsLoading) return undefined;

    let cancelled = false;
    let timeout;
    const poll = async () => {
      try {
        const next = await api.getExpediente(cuij);
        if (!cancelled) {
          setExpediente((current) =>
            mergeExpedienteSummary(current, next),
          );
        }
      } catch {
        // La carga principal ya tiene sus propios estados. Un fallo transitorio
        // de polling se vuelve a intentar sin reemplazar los datos visibles.
      }
      if (!cancelled) timeout = window.setTimeout(poll, 1500);
    };
    timeout = window.setTimeout(poll, 1000);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [api, cuij, detailsLoading]);

  useEffect(() => {
    if (!isManagedExpediente) {
      setAiReadState(null);
      return undefined;
    }
    let cancelled = false;
    let timeout;
    const poll = async () => {
      try {
        const next = await api.getExpedienteIntelligence(cuij);
        if (cancelled) return;
        setAiReadState(next);
        if (["pending", "processing"].includes(next?.progress?.status)) {
          timeout = window.setTimeout(poll, 1500);
        }
      } catch {
        if (!cancelled) timeout = window.setTimeout(poll, 3000);
      }
    };
    void poll();
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [
    api,
    cuij,
    isManagedExpediente,
    expediente?.dataSync?.generation,
    expediente?.dataSync?.aiRead?.status,
    aiReadState?.progress?.status,
    detailsLoading,
  ]);

  const runRefresh = async ({ reportError = true, force = true, aiCheck = false } = {}) => {
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    setRefreshing(true);
    if (aiCheck) setAiReadChecking(true);
    if (reportError) setError("");
    try {
      const next = await api.refreshExpediente(cuij, { force });
      setExpediente((current) => mergeExpedienteSummary(current, next));
    } catch (requestError) {
      if (reportError) setError(requestError.message);
    } finally {
      refreshInFlightRef.current = false;
      setRefreshing(false);
      if (aiCheck) setAiReadChecking(false);
    }
  };

  const refresh = () => runRefresh({ reportError: true, force: true });

  useEffect(() => {
    if (
      loading ||
      !expediente ||
      !isManagedExpediente ||
      detailsLoading ||
      automaticRefreshCuijRef.current === cuij
    ) {
      return;
    }

    automaticRefreshCuijRef.current = cuij;
    if (hasRecentExpedienteCheck(expediente)) return;
    void runRefresh({ reportError: false, force: false, aiCheck: true });
  }, [
    cuij,
    loading,
    expediente?._id,
    expediente?.detailsRefreshedAt,
    expediente?.praxisRefreshedAt,
    detailsLoading,
    isManagedExpediente,
  ]);

  const retryAiRead = async () => {
    if (retryingAiRead) return;
    setRetryingAiRead(true);
    try {
      setError("");
      setAiReadState(await api.retryExpedienteIntelligence(cuij));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setRetryingAiRead(false);
    }
  };

  const retryDetails = async () => {
    setRetryingDetails(true);
    setError("");
    try {
      const next = await api.retryExpedienteDetails(cuij);
      setExpediente((current) => mergeExpedienteSummary(current, next));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setRetryingDetails(false);
    }
  };

  const jumpToExpediente = (event) => {
    event.preventDefault();
    const formatted = formatCuij(jumpCuij);
    if (!isValidCuij(formatted)) {
      setJumpError("Ingresá un CUIJ válido.");
      return;
    }
    setJumpError("");
    if (formatted === cuij) {
      setJumpCuij("");
      return;
    }
    navigate(`${detailBasePath}/${encodeCuijPath(formatted)}`);
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      setExpediente(await api.addExpediente(expediente.cuij));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const addToFollowUps = async () => {
    if (addingFollowUp || expediente.isFollowed) return;
    setAddingFollowUp(true);
    setError("");
    try {
      const [followUp] = await api.addFollowUps([expediente.cuij]);
      setExpediente((current) => ({
        ...current,
        isFollowed: true,
        followUpId: followUp.id,
        manualReminders: followUp.reminders || [],
      }));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setAddingFollowUp(false);
    }
  };

  const removeFollowUp = async () => {
    if (!expediente.followUpId) return;
    setRemovingFollowUp(true);
    setError("");
    try {
      await api.removeFollowUp(expediente.followUpId);
      navigate("/seguimientos");
    } catch (requestError) {
      setError(requestError.message);
      setRemoveFollowUpOpen(false);
    } finally {
      setRemovingFollowUp(false);
    }
  };

  const changeArchiveStatus = async () => {
    if (!archiveAction) return;
    setChangingArchive(true);
    setError("");
    try {
      const next =
        archiveAction === "archive"
          ? await api.archiveExpediente(expediente.cuij)
          : await api.restoreExpediente(expediente.cuij);
      setExpediente(next);
      setArchiveAction(null);
    } catch (requestError) {
      setError(requestError.message);
      setArchiveAction(null);
    } finally {
      setChangingArchive(false);
    }
  };

  const removeSavedExpediente = async () => {
    setRemovingSaved(true);
    setError("");
    try {
      await api.removeExpediente(expediente.cuij);
      navigate("/expedientes");
    } catch (requestError) {
      setError(requestError.message);
      setRemoveSavedOpen(false);
    } finally {
      setRemovingSaved(false);
    }
  };

  const openActuacionDocument = (actuacion, documentCuij = cuij) => {
    const actId = actuacionDocumentId(actuacion);
    if (!actId) return;
    const sourceCuij = documentCuij || cuij;
    const attachmentId = actuacion.documentAttachmentId || "";
    setDocumentDialog(null);
    navigate(
      buildDocumentViewerRoute(path, routeSearch, {
        actId,
        attachmentId,
        cuij: sourceCuij,
        includeCuij: normalizeCuij(sourceCuij) !== normalizeCuij(cuij),
      }),
      {
        state: {
          ...window.history.state,
          [DOCUMENT_VIEWER_HISTORY_STATE]: true,
        },
      },
    );
  };

  const openAttachment = (attachment) => {
    if (documentDialog?.kind !== "attachments") return;
    navigate(
      buildDocumentViewerRoute(path, routeSearch, {
        actId: documentDialog.actId,
        attachmentId: attachment.id,
        cuij: routedDocument.cuij,
        includeCuij:
          normalizeCuij(routedDocument.cuij) !== normalizeCuij(cuij),
      }),
      {
        replace: true,
        state: {
          ...window.history.state,
          [DOCUMENT_VIEWER_HISTORY_STATE]: true,
        },
      },
    );
  };

  const allTimelineItems = useMemo(
    () => buildTimelineItems(expediente),
    [expediente?.actuaciones, expediente?.movimientos],
  );
  const dashboardActuaciones = useMemo(
    () => selectDashboardActuaciones(allTimelineItems),
    [allTimelineItems],
  );
  const timelineTotalCount = timelineLoaded
    ? allTimelineItems.length
    : (expediente?.actuacionesCount || 0)
      + (expediente?.movimientosCount || 0);

  const timelineCategoryCounts = useMemo(
    () =>
      allTimelineItems.reduce(
        (counts, item) => ({
          ...counts,
          [item.category]: (counts[item.category] || 0) + 1,
        }),
        {},
      ),
    [allTimelineItems],
  );
  const partyGroups = useMemo(
    () => analyzeExpedienteParties(expediente || {}, workspaceLawyers),
    [expediente?.partes, expediente?.caratula, workspaceLawyers],
  );
  const representedPartyGroups = useMemo(
    () => aiReadState?.perspective?.representedPartyGroups ||
      ["actor", "defendant"].filter((group) =>
        partyGroups[group].some(({ hasRosterMatch }) => hasRosterMatch),
      ),
    [aiReadState?.perspective?.representedPartyGroups, partyGroups],
  );
  const relatedExpedientes = useMemo(
    () => uniqueRelatedExpedientes(expediente?.relacionados || []),
    [expediente?.relacionados],
  );
  const relatedSentenceSourceKey = useMemo(
    () => relatedExpedientes
      .filter(({ cuij: relatedCuij }) => (
        normalizeCuij(relatedCuij)
        && normalizeCuij(relatedCuij) !== normalizeCuij(cuij)
      ))
      .map(({ cuij: relatedCuij, relationTypes = [] }) => (
        `${normalizeCuij(relatedCuij)}:${relationTypes.join(",")}`
      ))
      .sort()
      .join("|"),
    [cuij, relatedExpedientes],
  );

  useEffect(() => {
    if (
      !sourceAdapter.manageable ||
      activeDetailTab !== "documents" ||
      !expediente?.cuij
    ) return undefined;
    const sources = relatedExpedientes.filter(({ cuij: relatedCuij }) => (
      normalizeCuij(relatedCuij)
      && normalizeCuij(relatedCuij) !== normalizeCuij(cuij)
    ));
    if (!sources.length) {
      setRelatedSentenceSources([]);
      setRelatedSentencesLoading(false);
      setRelatedSentencesError("");
      return undefined;
    }

    let cancelled = false;
    setRelatedSentenceSources([]);
    setRelatedSentencesLoading(true);
    setRelatedSentencesError("");

    const loadSource = async (source) => {
      let timeline = null;
      for (let attempt = 0; attempt < 6; attempt += 1) {
        timeline = await api.getExpedienteTimeline(source.cuij);
        const status = timeline?.dataSync?.details?.status;
        if (!["pending", "processing"].includes(status)) break;
        await new Promise((resolve) => window.setTimeout(resolve, 1200));
        if (cancelled) return null;
      }
      return {
        cuij: timeline?.cuij || source.cuij,
        caratula: source.caratula || null,
        relationTypes: source.relationTypes || [],
        actuaciones: timeline?.actuaciones || [],
        detailsFailed: timeline?.dataSync?.details?.status === "failed",
        detailsIncomplete: ["pending", "processing"].includes(
          timeline?.dataSync?.details?.status,
        ),
      };
    };

    Promise.allSettled(sources.map(loadSource))
      .then((results) => {
        if (cancelled) return;
        const loaded = results
          .filter(({ status, value }) => status === "fulfilled" && value)
          .map(({ value }) => value);
        const failures = results.filter(({ status }) => status === "rejected").length
          + loaded.filter(({ detailsFailed, detailsIncomplete }) => (
            detailsFailed || detailsIncomplete
          )).length;
        setRelatedSentenceSources(loaded);
        if (failures) {
          setRelatedSentencesError(
            failures === sources.length
              ? "No pudimos consultar las sentencias de los expedientes relacionados."
              : "Algunos expedientes relacionados no pudieron consultarse.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setRelatedSentencesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    activeDetailTab,
    api,
    cuij,
    expediente?.cuij,
    relatedSentenceSourceKey,
    sourceAdapter,
  ]);

  const aiSentenceDocuments = useMemo(() => {
    return (aiReadState?.intelligence?.records || [])
      .filter((record) => record.documentKind === "sentencia")
      .map((record) => ({
        milestoneId: `ai:${record.sourceId}`,
        instance: "Sentencia",
        label: record.documentLabel || "Sentencia",
        actuacion: {
          ...(record.metadata || {}),
          actId: record.metadata?.actId || record.sourceId,
          titulo:
            record.documentLabel ||
            record.metadata?.titulo ||
            "Sentencia",
        },
      }));
  }, [aiReadState?.intelligence?.records]);
  const ownSentenceDocuments = useMemo(() => {
    const documentsByActuation = new Map();
    for (const actuacion of expediente?.actuaciones || []) {
      const detection = detectSentenceActuacion(actuacion);
      if (!detection.isSentence) continue;
      const documentId = actuacionDocumentId(actuacion)
        || `${actuacion.numero || "sin-numero"}:${actuacion.fechaFirma || actuacion.fechaPub || "sin-fecha"}`;
      documentsByActuation.set(documentId, {
        milestoneId: `metadata:${documentId}`,
        instance: detection.confidence === "high"
          ? "Sentencia · metadatos PJM"
          : "Posible sentencia · metadatos PJM",
        label: actuacion.titulo || "Sentencia",
        actuacion,
        sourceCuij: expediente?.cuij || cuij,
        relationTypes: [],
        isRelated: false,
      });
    }
    for (const document of aiSentenceDocuments) {
      const documentId = actuacionDocumentId(document.actuacion)
        || document.milestoneId;
      documentsByActuation.set(documentId, {
        ...document,
        sourceCuij: expediente?.cuij || cuij,
        relationTypes: [],
        isRelated: false,
      });
    }
    return [...documentsByActuation.values()].sort((left, right) => {
      const leftDate = new Date(
        left.actuacion.fechaFirma || left.actuacion.fechaPub || 0,
      ).getTime() || 0;
      const rightDate = new Date(
        right.actuacion.fechaFirma || right.actuacion.fechaPub || 0,
      ).getTime() || 0;
      return rightDate - leftDate;
    });
  }, [aiSentenceDocuments, cuij, expediente?.actuaciones, expediente?.cuij]);
  const sentenceDocuments = useMemo(() => {
    const documentsByActuation = new Map();
    for (const document of ownSentenceDocuments) {
      const documentId = actuacionDocumentId(document.actuacion)
        || document.milestoneId;
      documentsByActuation.set(
        `${normalizeCuij(document.sourceCuij)}:${documentId}`,
        document,
      );
    }
    for (const source of relatedSentenceSources) {
      for (const actuacion of source.actuaciones) {
        const detection = detectSentenceActuacion(actuacion);
        if (!detection.isSentence) continue;
        const documentId = actuacionDocumentId(actuacion)
          || `${actuacion.numero || "sin-numero"}:${actuacion.fechaFirma || actuacion.fechaPub || "sin-fecha"}`;
        const sourceKey = normalizeCuij(source.cuij);
        documentsByActuation.set(`${sourceKey}:${documentId}`, {
          milestoneId: `related:${sourceKey}:${documentId}`,
          instance: detection.confidence === "high"
            ? "Sentencia · metadatos PJM"
            : "Posible sentencia · metadatos PJM",
          label: actuacion.titulo || "Sentencia",
          actuacion,
          sourceCuij: source.cuij,
          sourceCaratula: source.caratula,
          relationTypes: source.relationTypes,
          isRelated: true,
        });
      }
    }
    return [...documentsByActuation.values()].sort((left, right) => {
      const leftDate = new Date(
        left.actuacion.fechaFirma || left.actuacion.fechaPub || 0,
      ).getTime() || 0;
      const rightDate = new Date(
        right.actuacion.fechaFirma || right.actuacion.fechaPub || 0,
      ).getTime() || 0;
      return rightDate - leftDate;
    });
  }, [ownSentenceDocuments, relatedSentenceSources]);
  const sourceDocuments = useMemo(
    () => (expediente?.documentos || []).map((document, index) => {
      const actuacion = (expediente?.actuaciones || []).find(
        (item) => item.sourceDocumentId === document.id,
      ) || {
        actId: document.actionId || document.id,
        titulo: document.title,
        fechaPub: document.date,
        fechaFirma: document.date,
        dateOnly: true,
        documentOpenable: false,
      };
      return {
        milestoneId: document.id || `source-document:${index}`,
        instance: document.kind || "Documento",
        label: document.title || "Documento del expediente",
        actuacion,
        sourceCuij: expediente?.identifier || expediente?.cuij || cuij,
        relationTypes: [],
        isRelated: false,
      };
    }),
    [cuij, expediente?.actuaciones, expediente?.cuij, expediente?.documentos,
      expediente?.identifier],
  );
  const expertReports = useMemo(
    () =>
      (aiReadState?.intelligence?.records || []).flatMap((record) => {
        const classifiedDocuments = (record.documentClassifications || [])
          .filter(
            (classification) =>
              classification.documentKind === "informe_pericial",
          );
        const reports = classifiedDocuments.length
          ? classifiedDocuments
          : record.documentKind === "informe_pericial"
            ? [{
                documentSourceId: `legacy:${record.sourceId}`,
                documentLabel: record.documentLabel,
                author: record.author,
                attachmentId: null,
              }]
            : [];
        return reports.map((classification) => ({
          key: `ai:${record.sourceId}:${classification.documentSourceId}`,
          expertName:
            classification.author?.name || record.author?.name || "",
          specialty:
            classification.author?.role ||
            record.author?.role ||
            "Informe pericial",
          registration:
            classification.author?.registration ||
            record.author?.registration ||
            "",
          attachmentId: classification.attachmentId || null,
          submittedAt:
            record.metadata?.fechaFirma || record.metadata?.fechaPub || null,
          actuacion: {
            ...(record.metadata || {}),
            actId: record.metadata?.actId || record.sourceId,
            documentAttachmentId: classification.attachmentId || null,
            titulo:
              classification.documentLabel ||
              record.documentLabel ||
              record.metadata?.titulo ||
              "Informe pericial",
          },
          score: Math.round((classification.confidence ?? 1) * 100),
        }));
      }),
    [aiReadState?.intelligence?.records],
  );
  const contactDirectory = useMemo(
    () => aiReadState?.intelligence?.contactDirectory || [],
    [aiReadState?.intelligence?.contactDirectory],
  );
  const directoryEntries = useMemo(
    () => buildExpedienteDirectoryEntries({
      partyGroups,
      contactDirectory,
      actors: aiReadState?.intelligence?.actors || [],
      judge: expediente?.juez || null,
    }),
    [
      aiReadState?.intelligence?.actors,
      contactDirectory,
      expediente?.juez,
      partyGroups,
    ],
  );
  const dashboardContacts = useMemo(
    () => selectDashboardContacts(directoryEntries),
    [directoryEntries],
  );
  const whatsappTurnNotices = useMemo(
    () =>
      buildExpedienteWhatsappTurnNotices({
        expediente,
        expertProceedings:
          aiReadState?.intelligence?.procedure?.expertProceedings || [],
        procedureFacts: procedureState?.evaluation?.facts || [],
        actorName: partyGroups.actor[0]?.nombreApellido || null,
        today: todayInMendoza(),
      }),
    [
      aiReadState?.intelligence?.procedure?.expertProceedings,
      expediente?.caratula,
      expediente?.cuij,
      expediente?.numero,
      partyGroups.actor,
      procedureState?.evaluation?.facts,
    ],
  );
  const allIntelligenceInconsistencies = useMemo(
    () =>
      (aiReadState?.intelligence?.procedure?.inconsistencies || []).map(
        (issue) => ({
          ...issue,
          whatsappNotice: whatsappNoticeForInconsistency(
            issue,
            whatsappTurnNotices,
          ),
        }),
      ),
    [
      aiReadState?.intelligence?.procedure?.inconsistencies,
      whatsappTurnNotices,
    ],
  );
  const intelligenceInconsistencies = useMemo(
    () => allIntelligenceInconsistencies.filter(
      ({ acknowledgment }) => !acknowledgment,
    ),
    [allIntelligenceInconsistencies],
  );
  const acknowledgedIntelligenceInconsistencies = useMemo(
    () => allIntelligenceInconsistencies.filter(
      ({ acknowledgment }) => Boolean(acknowledgment),
    ),
    [allIntelligenceInconsistencies],
  );
  const dashboardImportantDeadlines = useMemo(
    () =>
      selectDashboardImportantDeadlines(
        procedureState?.deadlines,
        representedPartyGroups,
      ),
    [procedureState?.deadlines, representedPartyGroups],
  );
  const trafficState = useMemo(
    () =>
      expedienteTrafficState({
        deadlines: procedureState?.deadlines || [],
        manualReminders: expediente?.manualReminders || [],
        representedPartyGroups,
      }),
    [
      expediente?.manualReminders,
      procedureState?.deadlines,
      representedPartyGroups,
    ],
  );
  const reminderRefresh = reminderRefreshPresentation({
    procedureState,
    procedureLoading,
    hasFallbackContent:
      (expediente?.manualReminders || []).length > 0 ||
      intelligenceInconsistencies.length > 0,
  });
  const trafficLightLoading = reminderRefresh.loading;
  const deadlinesLoading = reminderRefresh.loading;
  const deadlinesUpdating = reminderRefresh.updating;
  const attentionLoading = deadlinesLoading;
  const trafficLightTargetTab = trafficLightLoading ? null : "reminders";
  const trafficLightTargetLabel = trafficLightTargetTab ? "Recordatorios" : null;
  const dashboardDocuments = useMemo(
    () => [
      ...ownSentenceDocuments.map((document) => ({
        key: `sentence:${document.milestoneId}`,
        title: document.actuacion.titulo || document.label,
        subtitle: document.instance,
        actuacion: document.actuacion,
      })),
      ...expertReports.map((report) => ({
        key: `expert:${report.key}`,
        title:
          report.actuacion.titulo ||
          "Informe de perito",
        subtitle:
          report.expertName ||
          report.specialty ||
          "Perito no identificado",
        actuacion: report.actuacion,
      })),
    ],
    [expertReports, ownSentenceDocuments],
  );
  const documentsLoading =
    !sentenceDocuments.length &&
    !expertReports.length &&
    (!timelineLoaded ||
      timelineLoading ||
      procedureLoading ||
      relatedSentencesLoading);

  const activeTimelineHighlightPatterns = useMemo(
    () => (search.trim() ? [search.trim()] : []),
    [search],
  );
  const timelineItems = useMemo(
    () =>
      allTimelineItems.filter((item) =>
        activeTimelineFilters.includes(item.category),
      ),
    [activeTimelineFilters, allTimelineItems],
  );

  const timelineSearchMatches = useMemo(() => {
    const term = search.trim();
    if (!term) return [];
    return allTimelineItems.filter((item) =>
      matchesApproximateText(timelineSearchValues(item), term),
    );
  }, [allTimelineItems, search]);

  const toggleTimelineFilter = (filterId) => {
    setActiveTimelineFilters((current) => {
      if (!current.includes(filterId)) return [...current, filterId];
      if (current.length === 1) return current;
      return current.filter((id) => id !== filterId);
    });
  };

  const scrollToTimelineItem = (item) => {
    if (!item) return;
    window.clearTimeout(timelineHighlightTimeoutRef.current);
    setHighlightedTimelineEntry(null);
    setActiveTimelineFilters((current) =>
      current.includes(item.category) ? current : [...current, item.category],
    );
    setSearch("");
    setSearchOpen(false);

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const target = document.getElementById(timelineDomId(item));
        if (!target) return;
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        target.focus({ preventScroll: true });
        setHighlightedTimelineEntry(item.entryId);
        timelineHighlightTimeoutRef.current = window.setTimeout(() => {
          setHighlightedTimelineEntry(null);
        }, 3000);
      });
    });
  };

  const revealTimelineSource = (sourceId) => {
    if (!sourceId) return;
    navigate(
      expedienteDetailTabPath(cuij, "actuaciones", detailBasePath),
    );
    setTimelineExpanded(true);
    pendingTimelineSourceRef.current = String(sourceId);
    const target = allTimelineItems.find((item) =>
      timelineSourceIds(item).includes(String(sourceId)),
    );
    if (!target) void loadTimeline();
  };

  useEffect(() => {
    const sourceId = pendingTimelineSourceRef.current;
    if (
      activeDetailTab !== "actuaciones" ||
      !sourceId ||
      !allTimelineItems.length
    ) {
      return;
    }
    const target = allTimelineItems.find((item) =>
      timelineSourceIds(item).includes(sourceId),
    );
    if (!target) return;
    pendingTimelineSourceRef.current = "";
    scrollToTimelineItem(target);
  }, [activeDetailTab, allTimelineItems]);

  useEffect(() => {
    if (
      !routeActuacionId ||
      routeActuacionHandledRef.current === routeActuacionId
    ) {
      return;
    }
    const target = allTimelineItems.find((item) =>
      timelineSourceIds(item).includes(String(routeActuacionId)),
    );
    if (!target) return;
    routeActuacionHandledRef.current = routeActuacionId;
    if (activeDetailTab !== "actuaciones") {
      navigate(
        `${expedienteDetailTabPath(cuij, "actuaciones", detailBasePath)}?actuacion=${encodeURIComponent(routeActuacionId)}`,
        { replace: true },
      );
    }
    scrollToTimelineItem(target);
  }, [
    activeDetailTab,
    allTimelineItems,
    cuij,
    detailBasePath,
    navigate,
    routeActuacionId,
  ]);

  const openDraftGenerator = useCallback(async (draft) => {
    const created = await api.createWorkspaceDocument({
      productId: "praxis",
      documentType: draft.kind === "message" ? "message" : "legal_filing",
      title: draft.title || draft.reminderLabel || "Nuevo escrito",
      content: toDocument(draft.body || ""),
      context: { type: "expediente", id: cuij },
      origin: {
        type: "reminder",
        referenceId: draft.deadlineId || null,
        metadata: {
          reminderLabel: draft.reminderLabel || "",
          recipient: draft.recipient || "",
          notice: draft.notice || "",
        },
      },
    });
    navigate(
      `${expedienteDetailTabPath(cuij, "drafts", detailBasePath)}/${created.id}`,
    );
    return created;
  }, [api, cuij, detailBasePath, navigate, toDocument]);

  if (loading) {
    return (
      <div className="page expediente-detail-page">
        <div className="expediente-lookup-status">
          <LoaderCircle className="spin" size={20} />
          <div>
            <strong>Buscando el expediente</strong>
            <span>
              Si todavía no está en {productName}, lo consultaremos en el Poder
              Judicial. Esto puede demorar unos segundos.
            </span>
          </div>
        </div>
        <div className="loading-list expediente-detail-loading">
          <span />
          <span />
          <span />
        </div>
      </div>
    );
  }

  if (!expediente) {
    return (
      <div className="page expediente-detail-page">
        <button
          className="back-link"
          onClick={() =>
            navigate(resolvedBackPath)
          }
        >
          <ArrowLeft size={17} />
          {resolvedBackLabel}
        </button>
        <div className="inline-error">
          <span>{error || "No pudimos cargar el expediente."}</span>
          <button onClick={load}>Reintentar</button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`page expediente-detail-page ${
        activeDetailTab === "dashboard" ? "is-dashboard" : ""
      }`}
    >
      <div className="expediente-detail-topbar">
        <div className="expediente-detail-navigation">
          <button
            className="back-link"
            onClick={() =>
              navigate(resolvedBackPath)
            }
          >
            <ArrowLeft size={17} />
            {resolvedBackLabel}
          </button>
          {sourceAdapter.manageable && !readOnly && <form
            className="expediente-jump-form"
            onSubmit={jumpToExpediente}
          >
            <div className="expediente-jump-control">
              <Search size={15} aria-hidden="true" />
              <input
                id="expediente-jump-cuij"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                aria-label="Ir a otro expediente por CUIJ"
                placeholder="Ir a CUIJ…"
                value={jumpCuij}
                aria-invalid={Boolean(jumpError)}
                aria-describedby={
                  jumpError ? "expediente-jump-error" : undefined
                }
                onChange={(event) => {
                  setJumpCuij(formatCuij(event.target.value));
                  setJumpError("");
                }}
              />
              <button type="submit" disabled={!jumpCuij}>
                Ir
              </button>
            </div>
            {jumpError && (
              <span id="expediente-jump-error" role="alert">
                {jumpError}
              </span>
            )}
          </form>}
        </div>
        {(isManagedExpediente || (sourceAdapter.manageable && !readOnly)) && (
          <div className="expediente-detail-actions">
            {sourceAdapter.manageable && !readOnly && (
              <>
                {isFollowUpContext && expediente.isFollowed ? (
                  <>
                    <span className="saved-expediente-badge">
                      <CheckCircle2 size={15} />
                      En Mis seguimientos
                    </span>
                    <button
                      className="button secondary"
                      onClick={() => setRemoveFollowUpOpen(true)}
                    >
                      <X size={17} />
                      Quitar de Mis seguimientos
                    </button>
                  </>
                ) : expediente.isArchived ? (
                  <button
                    className="button primary"
                    onClick={() => setArchiveAction("restore")}
                  >
                    <RotateCcw size={17} />
                    Reactivar expediente
                  </button>
                ) : !expediente.isSaved ? (
                  <button
                    className="button primary"
                    onClick={save}
                    disabled={saving}
                  >
                    {saving ? (
                      <LoaderCircle className="spin" size={17} />
                    ) : (
                      <Plus size={17} />
                    )}
                    {saving ? "Agregando…" : "Agregar a Mis expedientes"}
                  </button>
                ) : (
                  <button
                    className="button secondary"
                    onClick={() => setArchiveAction("archive")}
                  >
                    <Archive size={17} />
                    Archivar
                  </button>
                )}
                {!isFollowUpContext && (
                  expediente.isFollowed ? (
                    <span className="saved-expediente-badge">
                      <CheckCircle2 size={15} />
                      En Mis seguimientos
                    </span>
                  ) : (
                    <button
                      className="button secondary"
                      onClick={addToFollowUps}
                      disabled={addingFollowUp}
                    >
                      {addingFollowUp ? (
                        <LoaderCircle className="spin" size={17} />
                      ) : (
                        <CalendarClock size={17} />
                      )}
                      {addingFollowUp
                        ? "Agregando…"
                        : "Agregar a Mis seguimientos"}
                    </button>
                  )
                )}
                {!(isFollowUpContext && expediente.isFollowed) &&
                  (expediente.isArchived || expediente.isSaved) && (
                    <button
                      className="button danger"
                      onClick={() => setRemoveSavedOpen(true)}
                    >
                      <X size={17} />
                      Quitar de Mis expedientes
                    </button>
                  )}
              </>
            )}
            {isManagedExpediente && (
              <AiReadProgress
                state={aiReadState}
                checking={aiReadChecking || refreshing || detailsLoading}
                retrying={retryingAiRead}
                onRetry={retryAiRead}
              />
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="inline-error">
          <span>{error}</span>
          <button onClick={sourceAdapter.manageable && !readOnly ? refresh : load}>
            Reintentar
          </button>
        </div>
      )}

      {detailsFailed && (
        <div
          className={`expediente-details-status ${
            detailsFailed ? "failed" : ""
          }`}
          aria-live="polite"
        >
          <RefreshCw size={19} />
          <div>
            <strong>
              No pudimos completar toda la información
            </strong>
            <span>
              {expediente.dataSync?.details?.error || "La consulta de los detalles no pudo completarse."}
            </span>
          </div>
          {detailsFailed && !readOnly && (
            <button
              className="button secondary"
              type="button"
              onClick={retryDetails}
              disabled={retryingDetails}
            >
              {retryingDetails && (
                <LoaderCircle className="spin" size={16} />
              )}
              {retryingDetails ? "Reintentando…" : "Reintentar detalles"}
            </button>
          )}
        </div>
      )}

      {archiveAction && (
        <ConfirmDialog
          title={
            archiveAction === "archive"
              ? "Archivar expediente"
              : "Reactivar expediente"
          }
          description={
            archiveAction === "archive"
              ? "Dejará de verse en la lista activa, pero seguirá en Mis expedientes y continuará recibiendo actualizaciones y análisis IA. Podrás encontrarlo en Archivados."
              : "Volverá a mostrarse en la lista activa de Mis expedientes."
          }
          confirmLabel={
            archiveAction === "archive"
              ? "Archivar expediente"
              : "Reactivar expediente"
          }
          tone={archiveAction === "archive" ? "danger" : "primary"}
          busy={changingArchive}
          onConfirm={changeArchiveStatus}
          onClose={() => setArchiveAction(null)}
        />
      )}

      {removeSavedOpen && (
        <ConfirmDialog
          title="Quitar de Mis expedientes"
          description="Esta acción elimina el expediente de este workspace y también lo quita de Mis seguimientos. Se borrarán sus recordatorios manuales, estados, correcciones de recordatorios automáticos y checkpoints asociados. No se puede deshacer, aunque podrás agregar el expediente nuevamente desde cero."
          confirmLabel="Quitar y borrar datos"
          tone="danger"
          busy={removingSaved}
          onConfirm={removeSavedExpediente}
          onClose={() => setRemoveSavedOpen(false)}
        />
      )}

      {removeFollowUpOpen && (
        <ConfirmDialog
          title="Quitar de Mis seguimientos"
          description="El expediente dejará de aparecer en Mis seguimientos y se eliminarán sus recordatorios manuales. Si también está en Mis expedientes, continuará allí con sus funciones automáticas."
          confirmLabel="Quitar seguimiento"
          tone="danger"
          busy={removingFollowUp}
          onConfirm={removeFollowUp}
          onClose={() => setRemoveFollowUpOpen(false)}
        />
      )}

      <ActuacionDocumentDialog
        dialog={documentDialog}
        busy={attachmentLoading}
        onClose={() => {
          if (documentDialog?.routed) closeRoutedDocument();
          else setDocumentDialog(null);
        }}
        onSelectAttachment={openAttachment}
      />

      {pdfViewer && (
        <PdfViewer
          document={pdfViewer}
          onClose={closeRoutedDocument}
        />
      )}

      <header className="expediente-detail-hero">
        <div className="expediente-hero-kicker">
          <CopyableText
            className="expediente-cuij-copy"
            value={expediente.identifier || expediente.cuij}
            label={sourceAdapter.identifierLabel}
          >
            {expediente.identifier || expediente.cuij}
          </CopyableText>
          {expediente.numero && (
            <CopyableText
              className="expediente-hero-number"
              value={expediente.numero}
              label="número de expediente"
            >
              Expte. N.º {expediente.numero}
            </CopyableText>
          )}
          <a
            className="judiciary-expediente-link"
            href={sourceAdapter.externalUrl(expediente)}
            target="_blank"
            rel="noreferrer"
            aria-label={`Ver el expediente ${expediente.identifier || expediente.cuij} en ${expediente.sourceName || "el Poder Judicial"}`}
          >
            {expediente.sourceName || "Poder Judicial"}
            <ExternalLink size={12} aria-hidden="true" />
          </a>
          {expediente.tieneSentencia && (
            <span className="sentence-badge">
              <CheckCircle2 size={13} /> Con sentencia
            </span>
          )}
        </div>
        <div className="expediente-title-row">
          <h1>{expediente.caratula || "Sin carátula informada"}</h1>
        </div>
        {isManagedExpediente && (
          <button
            type="button"
            className={`expediente-traffic-light ${
              trafficLightLoading
                ? "is-loading"
                : `is-${trafficState.expedienteColor}`
            }`}
            aria-label={
              trafficLightLoading
                ? "Calculando el semáforo del expediente"
                : `Semáforo del expediente: ${trafficState.expedienteColor}. Ir a ${trafficLightTargetLabel}`
            }
            title={
              trafficLightLoading
                ? "Calculando estado"
                : `Estado ${trafficState.expedienteColor} · Ir a ${trafficLightTargetLabel}`
            }
            disabled={!trafficLightTargetTab}
            onClick={() =>
              navigate(
                expedienteDetailTabPath(
                  cuij,
                  trafficLightTargetTab,
                  detailBasePath,
                ),
              )
            }
          />
        )}
        <p>
          <Landmark size={16} />
          {expediente.organismoActual || "Organismo no informado"}
        </p>
        <div className="last-refresh-banner">
          <CalendarClock size={18} />
          <span>
            <small>
              {expediente.detailsRefreshedAt || expediente.praxisRefreshedAt
                ? "Última actualización completa"
                : "Datos básicos actualizados"}
            </small>
            <strong>
              <RenderedDate
                value={
                  expediente.detailsRefreshedAt ||
                  expediente.praxisRefreshedAt ||
                  expediente.basicRefreshedAt
                }
                withTime
              />
            </strong>
          </span>
        </div>
      </header>

      {isManagedExpediente && aiReadState?.intelligence?.situation && (
        <section
          className="expediente-ai-situation"
          aria-labelledby="expediente-ai-situation-title"
        >
          <span className="expediente-ai-situation-icon" aria-hidden="true">
            <Sparkles size={17} />
          </span>
          <div>
            <strong id="expediente-ai-situation-title">
              Estado evaluado por IA
            </strong>
            <p>{aiReadState.intelligence.situation}</p>
          </div>
        </section>
      )}

      <nav
        className="expediente-detail-tabs"
        style={{ "--expediente-detail-tab-count": visibleDetailTabs.length }}
        role="tablist"
        aria-label="Secciones del expediente"
      >
        {visibleDetailTabs.map((tab) => (
          <AppLink
            key={tab.id}
            id={`expediente-tab-${tab.id}`}
            className={`expediente-detail-tab ${
              activeDetailTab === tab.id ? "is-active" : ""
            }`}
            activeClassName=""
            exact
            to={expedienteDetailTabPath(
              cuij,
              tab.id,
              detailBasePath,
            )}
            role="tab"
            aria-selected={activeDetailTab === tab.id}
            aria-current={activeDetailTab === tab.id ? "page" : undefined}
            aria-controls={`expediente-panel-${tab.id}`}
          >
            <span>{tab.label}</span>
            {!trafficLightLoading &&
              tab.id === "reminders" &&
              trafficState.deadlineColor !== "green" && (
                <span
                  className={`expediente-tab-status-dot is-${trafficState.deadlineColor}`}
                  aria-label={`Recordatorios en estado ${trafficState.deadlineColor}`}
                />
              )}
          </AppLink>
        ))}
      </nav>

      {activeDetailTab === "dashboard" && (
        <ExpedienteDashboard
          cuij={cuij}
          detailBasePath={detailBasePath}
          attentionLoading={attentionLoading}
          attentionUpdating={deadlinesUpdating}
          importantDeadlines={dashboardImportantDeadlines}
          manualReminders={(expediente.manualReminders || []).filter(
            (reminder) => reminder.status !== "completed",
          )}
          representedPartyGroups={representedPartyGroups}
          documents={dashboardDocuments}
          documentCount={ownSentenceDocuments.length + expertReports.length}
          documentsLoading={documentsLoading}
          documentLoading={documentLoading}
          contacts={dashboardContacts}
          contactsLoading={detailsLoading && !hasDetails}
          actuaciones={dashboardActuaciones}
          actuacionesLoading={timelineLoading && !timelineLoaded}
          onOpenDeadline={(deadlineId) => {
            setSelectedDeadlineId(deadlineId);
            navigate(
              expedienteDetailTabPath(cuij, "reminders", detailBasePath),
            );
          }}
          onOpenDocument={openActuacionDocument}
          onOpenActuacion={(actuacion) => {
            const sourceId = timelineSourceIds(actuacion)[0];
            if (sourceId) revealTimelineSource(sourceId);
          }}
          manualReminderDraft={manualReminderDraft}
          onManualReminderDraftChange={(patch) =>
            setManualReminderDraft((current) => ({
              ...current,
              ...patch,
            }))
          }
          onAddManualReminder={(event) =>
            addManualReminder(event, { redirectToReminders: true })
          }
          manualReminderSaving={manualReminderSaving}
          inconsistencies={intelligenceInconsistencies}
          onNavigateEvidence={revealTimelineSource}
          inconsistencyAcknowledgmentSaving={
            inconsistencyAcknowledgmentSaving
          }
          onSetInconsistencyAcknowledgment={
            setInconsistencyAcknowledgment
          }
        />
      )}

      {activeDetailTab === "reminders" && (
        <div
          id="expediente-panel-reminders"
          className="expediente-detail-tab-panel"
          role="tabpanel"
          aria-labelledby="expediente-tab-reminders"
        >
          <AutomaticDeadlinesPanel
            deadlines={procedureState?.deadlines || []}
            loading={deadlinesLoading}
            updating={deadlinesUpdating}
            selectedDeadlineId={selectedDeadlineId}
            onNavigateEvidence={revealTimelineSource}
            representedPartyGroups={representedPartyGroups}
            canOverride={procedureState?.canOverrideDeadlines}
            overrideSaving={deadlineOverrideSaving}
            overrideError={deadlineOverrideError}
            onSetOverride={setDeadlineOverride}
            manualReminders={expediente.manualReminders || []}
            manualReminderDraft={manualReminderDraft}
            onManualReminderDraftChange={(patch) =>
              setManualReminderDraft((current) => ({
                ...current,
                ...patch,
              }))
            }
            onAddManualReminder={addManualReminder}
            onSetManualReminderStatus={setManualReminderStatus}
            onDeleteManualReminder={deleteManualReminder}
            manualReminderSaving={manualReminderSaving}
            manualReminderStatusSaving={manualReminderStatusSaving}
            manualReminderDeleting={manualReminderDeleting}
            onOpenGenerator={openDraftGenerator}
            acknowledgedInconsistencies={
              acknowledgedIntelligenceInconsistencies
            }
            inconsistencyAcknowledgmentSaving={
              inconsistencyAcknowledgmentSaving
            }
            onSetInconsistencyAcknowledgment={
              setInconsistencyAcknowledgment
            }
          />
        </div>
      )}

      {activeDetailTab === "drafts" && WorkspaceDrafts && (
        <div
          id="expediente-panel-drafts"
          className="expediente-detail-tab-panel expediente-drafts-panel"
          role="tabpanel"
          aria-labelledby="expediente-tab-drafts"
        >
          <WorkspaceDrafts
            api={api}
            basePath={expedienteDetailTabPath(
              cuij,
              "drafts",
              detailBasePath,
            )}
            contextCuij={cuij}
            expedientes={[expediente]}
            selectedDocumentId={routedWorkspaceDocumentId}
          />
        </div>
      )}

      {activeDetailTab === "documents" && (
        <div
          id="expediente-panel-documents"
          className="expediente-detail-tab-panel expediente-documents-panel"
          role="tabpanel"
          aria-labelledby="expediente-tab-documents"
        >
          {documentsLoading ? (
            <DetailsSectionLoading label="Buscando documentos" />
          ) : (
            <div className="expediente-documents-grid">
              <SentenceDocumentsBlock
                documents={sourceAdapter.manageable
                  ? sentenceDocuments
                  : sourceDocuments}
                documentLoading={documentLoading}
                relatedLoading={sourceAdapter.manageable
                  ? relatedSentencesLoading
                  : false}
                relatedError={sourceAdapter.manageable
                  ? relatedSentencesError
                  : ""}
                onOpenDocument={openActuacionDocument}
                title={sourceAdapter.manageable
                  ? "Sentencias"
                  : "Documentos judiciales"}
                singularDescription={sourceAdapter.manageable
                  ? "sentencia identificada"
                  : "documento identificado"}
                pluralDescription={sourceAdapter.manageable
                  ? "sentencias identificadas"
                  : "documentos identificados"}
                ariaLabel={sourceAdapter.manageable
                  ? "Sentencias del expediente y sus relacionados"
                  : "Documentos informados por la fuente judicial"}
                emptyMessage={sourceAdapter.manageable
                  ? "No se encontraron sentencias en este expediente ni en sus relacionados."
                  : "La fuente judicial no informó documentos para este expediente."}
              />
              {isManagedExpediente && (
                <ExpertReportsBlock
                  reports={expertReports}
                  documentLoading={documentLoading}
                  attachmentLoading={attachmentLoading}
                  onOpenDocument={openActuacionDocument}
                />
              )}
              {isManagedExpediente && <UploadedDocumentsBlock />}
            </div>
          )}
        </div>
      )}

      {activeDetailTab === "information" && (
        <div
          id="expediente-panel-information"
          className="expediente-detail-tab-panel"
          role="tabpanel"
          aria-labelledby="expediente-tab-information"
        >
      <section className="expediente-overview-grid">
        <DetailValue
          label="Número de expediente"
          value={expediente.numero}
          icon={FileText}
        />
        <DetailValue label="Fuero" value={expediente.fuero} icon={Gavel} />
        <DetailValue
          label="Fecha de inicio"
          value={<RenderedDate value={expediente.fechaInicio} />}
          icon={FileClock}
        />
        <div className="expediente-detail-value expediente-detail-objects">
          <span>
            <FileText size={15} /> Objeto del juicio
          </span>
          {expediente.objetoJuicios?.length ? (
            <div className="expediente-object-list">
              {expediente.objetoJuicios.map((objeto, index) => (
                <article key={`${objeto.tipo}-${index}`}>
                  <strong>{objeto.descripcion || objeto.tipo}</strong>
                  {objeto.materia && <small>{objeto.materia}</small>}
                </article>
              ))}
            </div>
          ) : (
            <strong>No informado</strong>
          )}
        </div>
      </section>

      <ExpedienteDirectoryBlock
        entries={directoryEntries}
        partyGroups={partyGroups}
        loading={detailsLoading}
      />

      <DataTableSection
        className="expediente-detail-table-section information-related-section"
        description={`${relatedExpedientes.length} expedientes`}
        icon={FileClock}
        title="Expedientes relacionados"
      >
          {detailsLoading && !hasDetails ? (
            <DetailsSectionLoading label="Cargando relacionados" />
          ) : relatedExpedientes.length ? (
            <RelatedExpedientesTable
              items={relatedExpedientes}
              onOpen={(relatedCuij) =>
                navigate(expedienteDetailPath(relatedCuij))
              }
            />
          ) : (
            <p className="muted-copy">No hay expedientes relacionados.</p>
          )}
      </DataTableSection>
        </div>
      )}

      {activeDetailTab === "actuaciones" && (
        <div
          id="expediente-panel-actuaciones"
          className="expediente-detail-tab-panel"
          role="tabpanel"
          aria-labelledby="expediente-tab-actuaciones"
        >
      <DataTableSection
        className="expediente-detail-table-section actuaciones-section"
        description={
          timelineExpanded && timelineLoaded
            ? `${timelineItems.length} de ${allTimelineItems.length} registros`
            : `${timelineTotalCount} registros`
        }
        icon={FileText}
        title="Actuaciones"
        actions={
          timelineExpanded ? (
            <div className="actuaciones-heading-actions">
              <aside
                className="actuaciones-search-wrapper"
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    setSearchOpen(false);
                  }
                }}
              >
                <label className="search-field actuaciones-search">
                  <Search size={17} />
                  <input
                    value={search}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={searchOpen && Boolean(search.trim())}
                    aria-controls="actuaciones-search-results"
                    onFocus={() => setSearchOpen(true)}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setSearchOpen(true);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") setSearchOpen(false);
                      if (
                        event.key === "ArrowDown" &&
                        timelineSearchMatches.length
                      ) {
                        event.preventDefault();
                        document
                          .querySelector(
                            "#actuaciones-search-results .actuacion-search-result",
                          )
                          ?.focus();
                      }
                    }}
                    placeholder="Buscar para ir a un registro"
                    aria-label="Buscar actuaciones y movimientos"
                  />
                </label>
                {searchOpen && search.trim() && (
                  <div
                    className="actuaciones-search-results"
                    id="actuaciones-search-results"
                    role="listbox"
                    aria-label="Resultados en actuaciones"
                  >
                    <header>
                      <strong>
                        {timelineSearchMatches.length
                          ? `${timelineSearchMatches.length} ${
                              timelineSearchMatches.length === 1
                                ? "resultado"
                                : "resultados"
                            }`
                          : "Sin resultados"}
                      </strong>
                      <span>Seleccioná uno para ir al registro</span>
                    </header>
                    {timelineSearchMatches.slice(0, 12).map((item) => (
                      <button
                        className={`actuacion-search-result timeline-kind-${item.category}`}
                        type="button"
                        role="option"
                        aria-selected="false"
                        key={item.entryId}
                        onClick={() => scrollToTimelineItem(item)}
                      >
                        <span className="actuacion-search-result-kind">
                          <i />
                          {item.entryType === "movimiento"
                            ? "Movimiento"
                            : getActuacionCategory(item)}
                        </span>
                        <strong>
                          <ApproximateMatchText
                            value={
                              item.entryType === "movimiento"
                                ? item.descripcion || "Movimiento"
                                : item.titulo || "Sin título informado"
                            }
                            patterns={[search.trim()]}
                          />
                        </strong>
                        <small>
                          <ApproximateMatchText
                            value={
                              item.entryType === "movimiento"
                                ? [item.origen, item.destino]
                                    .filter(Boolean)
                                    .join(" → ")
                                : item.numero
                                  ? `N.º ${item.numero}`
                                  : "Sin número informado"
                            }
                            patterns={[search.trim()]}
                          />
                          {" · "}
                          <RenderedDate
                            as="span"
                            value={item.occurredAt}
                            withTime
                          />
                        </small>
                      </button>
                    ))}
                    {timelineSearchMatches.length > 12 && (
                      <footer>
                        Mostrando los primeros 12 resultados. Afiná la
                        búsqueda para encontrar uno específico.
                      </footer>
                    )}
                    {!timelineSearchMatches.length && (
                      <p>No encontramos coincidencias en este expediente.</p>
                    )}
                  </div>
                )}
              </aside>
            </div>
          ) : null
        }
      >
        {timelineExpanded && (
          <>
            <div
              className="actuaciones-filter-bar"
              aria-label="Filtrar actuaciones"
            >
              {TIMELINE_FILTERS.map((filter) => {
                const active = activeTimelineFilters.includes(filter.id);
                return (
                  <button
                    className={`actuacion-filter-chip timeline-kind-${filter.id} ${
                      active ? "active" : ""
                    }`}
                    type="button"
                    key={filter.id}
                    aria-pressed={active}
                    onClick={() => toggleTimelineFilter(filter.id)}
                  >
                    <span />
                    {filter.label}
                    <small>{timelineCategoryCounts[filter.id] || 0}</small>
                  </button>
                );
              })}
            </div>
            {!timelineLoaded && !timelineError ? (
              <DetailsSectionLoading label="Cargando actuaciones y movimientos" />
            ) : timelineError ? (
              <div className="expediente-section-empty">
                <RefreshCw size={20} />
                <span>{timelineError}</span>
                <button
                  type="button"
                  className="button secondary"
                  onClick={() => loadTimeline({ force: true })}
                >
                  Reintentar
                </button>
              </div>
            ) : timelineItems.length ? (
              <ActuacionesTable
                items={timelineItems}
                documentLoading={Boolean(documentLoading)}
                loadingActId={documentLoading}
                onOpenDocument={openActuacionDocument}
                inconsistencies={intelligenceInconsistencies}
                onNavigateEvidence={revealTimelineSource}
                highlightedEntryId={highlightedTimelineEntry}
                highlightPatterns={activeTimelineHighlightPatterns}
              />
            ) : (
              <div className="expediente-section-empty">
                <Search size={20} />
                <span>
                  {activeTimelineFilters.length < TIMELINE_FILTERS.length
                    ? "No hay registros para los filtros seleccionados."
                    : "No hay actuaciones informadas."}
                </span>
              </div>
            )}
          </>
        )}
      </DataTableSection>
        </div>
      )}
    </div>
  );
}
